<!-- eslint-disable vue/no-side-effects-in-computed-properties -->
<template>
  <!-- Modern Header -->
  <nav
    class="fixed top-0 z-50 w-full bg-white/95 backdrop-blur-sm border-b border-gray-200 dark:bg-bgLightDark/95 dark:border-gray-700 shadow-sm"
  >
    <div class="px-4 py-3 lg:px-6">
      <div class="flex items-center justify-between">
        <!-- Left Section -->
        <div class="flex items-center space-x-4">
          <a
            href="/sold-list"
            class="flex items-center space-x-2 text-gray-700 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            <i class="fa-solid fa-chevron-right text-lg"></i>
            <span class="text-xl font-semibold">{{ $t("app_name") }}</span>
          </a>

          <!-- Dollar Price Badge -->
          <div
            class="hidden sm:flex items-center space-x-2 bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 rounded-full"
          >
            <i
              class="fa-solid fa-dollar-sign text-blue-600 dark:text-blue-400 text-sm"
            ></i>
            <span class="text-sm font-medium text-blue-700 dark:text-blue-300">
              {{ $t("dolar_price") }}: {{ formatNumber(getDolarPrice) }}
            </span>
          </div>
        </div>

        <!-- Right Section -->
        <div class="flex items-center space-x-3">
          <!-- Prev invoice navigation -->
          <button
            type="button"
            class="inline-flex items-center justify-center w-32 h-10 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 shadow-sm hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-400 dark:focus:ring-blue-500 transition"
            :title="$t('prev')"
            @click="goToPrevInvoice"
            v-if="
              $store.getters['checkUserAccess']({
                id: '19',
                action: 'edit',
              })
            "
          >
            <!-- Reversed arrow icon for POS as requested
            <i class="fa-solid" :class="[$store.getters['isRtl'] ? 'fa-angle-left' : 'fa-angle-right']"></i>
         -->
            <span>{{ $t("prev_invoice") }}</span>
          </button>
          <div class="w-2"></div>
          <!-- Calculator Button -->
          <button
            @click="!isLoadingData && openCalculator()"
            :disabled="isLoadingData"
            :class="[
              'flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 dark:focus:ring-blue-500',
              isLoadingData
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:scale-105',
            ]"
            :title="
              isLoadingData ? 'Please wait for page to load' : 'Calculator'
            "
          >
            <i class="fa-solid fa-calculator text-lg"></i>
          </button>

          <!-- Loading Indicator -->
          <div
            v-if="isLoadingData"
            class="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400"
          >
            <div
              class="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"
            ></div>
            <span>Loading...</span>
          </div>
        </div>
      </div>
    </div>
  </nav>
  <LoadingProgress v-show="isLoadingData" />

  <div
    class="mx-2 mt-14 mb-5 p-3 h-full bg-slate-100 dark:bg-bgFullDark rounded-2xl"
  >
    <div class="flex mb-4 justify-between">
      <p class="font-semibold text-xl text-textBlack dark:text-textWhite mx-3">
        {{ $t("new_sale") }}
      </p>
      <div>
        <label
          for="boxType"
          class="block mb-1 text-sm text-textBlack1 dark:text-textWhite1"
          >{{ $t("currency_invoice_type") }}</label
        >
        <div class="flex">
          <button
            id="boxType"
            class="bg-white border border-gray-300 text-textBlack1 placeholder-gray-400 text-base rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-32 p-1.5 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            @click="changeCurrencyType"
          >
            <i
              class="fa-solid fa-arrows-rotate text-primaryColor mx-1 text-sm"
            ></i>
            <span class="mx-1" v-show="cur_id == 1">{{ $t("dinar") }}</span>
            <span class="mx-1" v-show="cur_id == 2">{{ $t("dollar") }}</span>
          </button>
          <div class="w-2"></div>
          <button class="btn btn-outline" @click="showWhatsAppDialog">
            <i class="fa-brands fa-whatsapp text-green-500 text-2xl"></i>
          </button>
        </div>
      </div>
    </div>
    <div class="md:flex">
      <div class="w-full md:w-5/12 flex flex-col py-3 mx-auto md:mx-0">
        <div
          class="flex bg-white dark:bg-bgMeduimDark rounded-2xl p-3 w-full overflow-x-auto space-x-2 whitespace-nowrap mb-4"
        >
          <BaseInput
            :modelValue="$store.state.sale_pos.searchPos"
            @update:modelValue="handleSearchInput"
            :placeholder="$t('search_item')"
            :prefixIcon="'fa-solid fa-search'"
            class="w-full"
          />

          <div class="w-6"></div>

          <form @submit="enterBarcode">
            <BaseInput
              v-model="barcode"
              :placeholder="$t('barcode')"
              :prefixIcon="'fa-solid fa-barcode'"
              class="w-full"
            />
          </form>
        </div>
        <!-- Categories Dropdown -->
        <h2
          id="accordion-color-heading-categories"
          :class="
            isShowCategories ? 'border-primaryColor text-blue-600' : ''
          "
        >
          <button
            type="button"
            class="flex bg-slate-100 dark:bg-bgFullDark items-center justify-between w-full px-4 py-2 font-medium text-right text-textBlack1 dark:text-textWhite1 border-l-0 border-r-0 border border-gray-300 dark:border-gray-600 hover:border-primaryColor"
            @click="isShowCategories = !isShowCategories"
          >
            <span>{{ $t("items_categorys") }}</span>
            <svg
              data-accordion-icon
              class="w-3 h-3 rotate-180 shrink-0"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 10 6"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5 5 1 1 5"
              />
            </svg>
          </button>
        </h2>
        <div
          class="flex flex-wrap gap-2 bg-white dark:bg-bgMeduimDark rounded-2xl p-3 w-full overflow-x-auto space-x-2 whitespace-nowrap mb-4"
          v-if="isShowCategories"
        >
          <button
            :class="
              'px-3 py-1 text-sm bg-white dark:bg-bgMeduimDark text-textBlack2 dark:text-textWhite3 rounded-xl border ' +
              (this.$store.state.sale_pos.ca_id == ''
                ? 'border-primaryColor dark:border-primaryColor'
                : 'border-gray-300 dark:border-gray-600')
            "
            @click="this.$store.state.sale_pos.ca_id = ''"
          >
            {{ $t("all") }}
          </button>
          <button
            :class="
              'px-3 py-1 text-sm bg-white dark:bg-bgMeduimDark text-textBlack2 dark:text-textWhite3 rounded-xl border ' +
              (this.$store.state.sale_pos.ca_id == category.ca_id
                ? 'border-primaryColor dark:border-primaryColor'
                : 'border-gray-300 dark:border-gray-600')
            "
            v-for="(category, index) in this.$store.state.item_category
              .categorys"
            :key="index"
            @click="this.$store.state.sale_pos.ca_id = category.ca_id"
          >
            {{ category.ca_name }}
          </button>
        </div>

        <!-- Items Grid -->
        <h2
          id="accordion-color-heading-items"
          :class="
            isShowItems ? 'border-primaryColor text-blue-600' : ''
          "
        >
          <button
            type="button"
            class="flex bg-slate-100 dark:bg-bgFullDark items-center justify-between w-full px-4 py-2 font-medium text-right text-textBlack1 dark:text-textWhite1 border-l-0 border-r-0 border border-gray-300 dark:border-gray-600 hover:border-primaryColor"
            @click="isShowItems = !isShowItems"
          >
            <span>{{ $t("itemss") }}</span>
            <svg
              data-accordion-icon
              class="w-3 h-3 rotate-180 shrink-0"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 10 6"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5 5 1 1 5"
              />
            </svg>
          </button>
        </h2>
        <div
          class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pb-10"
          v-if="isShowItems"
        >
          <!-- item 1-->
          <div
            :class="
              'flex flex-col p-2 bg-white dark:bg-bgMeduimDark rounded-2xl items-center shadow-sm p-2 cursor-pointer ' +
              (checkHowMuchItemInCard(item.i_id) != 0
                ? 'border border-primaryColor dark:border-primaryColor'
                : 'border-gray-300 dark:border-gray-600')
            "
            v-for="(item, index) in filterItemsPos"
            :key="index"
          >
            <div class="mb-1">
              <button
                type="button"
                @click="removeItem(item.i_id)"
                class="text-left mb-1 mx-1"
                v-if="checkHowMuchItemInCard(item.i_id) != 0"
              >
                <i
                  class="fa-solid fa-trash text-center p-1 w-6 h-6 text-xs bg-red-200 dark:bg-red-300 rounded-md text-red-500 mx-auto my-auto"
                ></i>
              </button>

              <a
                class="text-left mb-1 mx-1"
                :href="'item-detail/' + item.i_id"
                target="_blank"
              >
                <i
                  class="fa-solid fa-eye text-center p-1 w-6 h-6 text-xs bg-blue-200 dark:bg-blue-300 rounded-md text-blue-500 mx-auto my-auto"
                ></i>
              </a>
            </div>
            <img
              :src="getItemImage(item)"
              alt=""
              class="mb-1 rounded-xl h-24 cursor-pointer"
              @click="addItemToInvoice(item)"
            />

            <p
              class="text-textBlack1 dark:text-textWhite1 text-sm text-center cursor-pointer"
              @click="addItemToInvoice(item)"
            >
              {{ item.i_name }}
            </p>

            <p
              class="text-textBlack2 dark:text-textWhite2 cursor-pointer"
              @click="addItemToInvoice(item)"
            >
              {{ formatNumber(item.i_sale_price) }}
              {{ this.$t(item.currency.cur_name) }}
            </p>
            <p
              class="text-textBlack1 dark:text-textWhite1 text-sm text-center cursor-pointer"
              @click="addItemToInvoice(item)"
            >
              {{ $t("remain") }} {{ item.i_quantity }}
            </p>
            <span
              v-show="checkHowMuchItemInCard(item.i_id) != 0"
              class="bg-green-500 px-3 py-2 text-white rounded-full text-xs mt-2"
              @click="addItemToInvoice(item)"
            >
              {{ checkHowMuchItemInCard(item.i_id) }}
            </span>
          </div>
        </div>
        <center>
          <span
            v-show="filterItemsPos.length == 0 && isLoadingData == false"
            class="text-textBlack1 dark:text-white"
          >
            {{ $t("no_item_found") }}
          </span>
        </center>
        <center>
          <img
            src="../../assets/loading.webp"
            style="width: 70px; height: 70px"
            v-show="isLoadingData"
          />
        </center>
      </div>

      <div class="w-4"></div>

      <div
        class="w-full md:w-7/12 my-3 py-3 bg-white dark:bg-bgMeduimDark rounded-2xl mb-20 md:mb-14 mx-auto md:mx-0"
      >
        <form action="">
          <!--sale extra information-->

          <div id="accordion-color">
            <h2
              id="accordion-color-heading-1"
              :class="isShowDetail ? 'border-primaryColor text-blue-600' : ''"
            >
              <button
                type="button"
                class="flex bg-slate-100 dark:bg-bgFullDark items-center justify-between w-full px-4 py-2 font-medium text-right text-textBlack1 dark:text-textWhite1 border-l-0 border-r-0 border border-gray-300 dark:border-gray-600 hover:border-primaryColor"
                @click="isShowDetail = !isShowDetail"
              >
                <span
                  >{{ $t("detail") }}
                  {{ in_type == 1 ? $t("sale_tak") : $t("sale_jwmla") }}</span
                >
                <svg
                  data-accordion-icon
                  class="w-3 h-3 rotate-180 shrink-0"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 10 6"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5 5 1 1 5"
                  />
                </svg>
              </button>
            </h2>

            <!-- extra sale information-->
            <div
              id="accordion-color-body-1"
              aria-labelledby="accordion-color-heading-1"
              v-show="isShowDetail"
            >
              <div class="p-5 grid grid-cols-2 gap-x-3">
                <DropdownInput
                  v-model="bo_id"
                  :label="$t('box')"
                  :placeholder="$t('choose_box_that_money_get_in')"
                  :rules="rules_dropdown"
                  :items="convertToListIdValueBoxes"
                  :isError="
                    checkRulesIsError(bo_id, 'add', this.rules_dropdown).isError
                  "
                  :errorMsg="
                    checkRulesIsError(bo_id, 'add', this.rules_dropdown)
                      .errorMsg
                  "
                  linkDrop="/all-box"
                />
                <DropdownInput
                  v-model="st_id"
                  :label="$t('sale_type')"
                  :placeholder="$t('choose_sale_type')"
                  :rules="rules_dropdown"
                  :items="convertToListIdValueSaleTypes"
                  :isError="
                    checkRulesIsError(st_id, 'add', this.rules_dropdown).isError
                  "
                  :errorMsg="
                    checkRulesIsError(st_id, 'add', this.rules_dropdown)
                      .errorMsg
                  "
                  linkDrop="/add-sale-type"
                />
                <div class="mb-6">
                  <div class="flex">
                    <div class="w-full">
                      <DropdownInput
                        v-model="cu_id"
                        :label="$t('customer')"
                        :placeholder="$t('choose_customer')"
                        :items="convertToListIdValueCustomers"
                        :rules="rules_dropdown"
                        :isError="
                          checkRulesIsError(cu_id, 'add', this.rules_dropdown).isError
                        "
                        :errorMsg="
                          checkRulesIsError(cu_id, 'add', this.rules_dropdown).errorMsg
                        "
                        @change="getCustomerDebt"
                      />

                      <span
                        v-show="isShowDebts"
                        class="text-sm text-textBlack2 dark:text-textWhite2"
                        >{{ $t("total_debt_dinar") }} :
                        {{ formatNumber(beforeDebtIQD) }}
                      </span>
                      <div class="w-2"></div>
                      <span
                        v-show="isShowDebts"
                        class="text-sm text-textBlack2 dark:text-textWhite2"
                        >{{ $t("total_debt_dollar") }} :
                        {{ formatNumber(beforeDebtUSD) }}
                      </span>
                    </div>
                    <div class="w-2"></div>
                    <a
                      class="w-2/12 lg:w-1/12 text-white text-2xl my-8 bg-primaryColor p-1 rounded-lg hover:shadow-md text-center"
                      target="_blank"
                      href="/customer"
                    >
                      +
                    </a>
                  </div>
                </div>

                <DropdownInput
                  v-model="pl_id"
                  :label="$t('sale_platform')"
                  :placeholder="$t('choose_sale_platform')"
                  :rules="rules_dropdown"
                  :items="convertToListIdValuePlatforms"
                  :isError="
                    checkRulesIsError(pl_id, 'add', this.rules_dropdown).isError
                  "
                  :errorMsg="
                    checkRulesIsError(pl_id, 'add', this.rules_dropdown)
                      .errorMsg
                  "
                  linkDrop="/sale-platform"
                />
                <BaseInput
                  v-model="created_at"
                  type="date"
                  :label="$t('sale_date')"
                  :placeholder="$t('sale_date')"
                  :rules="no_rule"
                  :isError="
                    checkRulesIsError(created_at, 'add', this.no_rule).isError
                  "
                  :errorMsg="
                    checkRulesIsError(created_at, 'add', this.no_rule).errorMsg
                  "
                />
                <div>
                  <label
                    for="currency"
                    class="block mb-2 text-base font-medium text-textBlack1 dark:text-textWhite1"
                    >{{ $t("invoice_type_sale") }}</label
                  >
                  <div class="flex mx-4 mt-4">
                    <div class="flex items-center ml-4">
                      <input
                        id="inline-radio"
                        type="radio"
                        value="1"
                        checked="true"
                        v-model="in_type"
                        name="inline-radio-group"
                        class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        @change="getPosData"
                      />
                      <label
                        for="inline-radio"
                        class="mr-2 text-base font-medium text-gray-900 dark:text-gray-300"
                        >{{ $t("sale_tak") }}</label
                      >
                    </div>
                    <div class="flex items-center ml-4">
                      <input
                        id="inline-2-radio"
                        type="radio"
                        value="2"
                        v-model="in_type"
                        @change="getPosData"
                        name="inline-radio-group"
                        class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label
                        for="inline-2-radio"
                        class="mr-2 text-base font-medium text-gray-900 dark:text-gray-300"
                        >{{ $t("sale_jwmla") }}</label
                      >
                    </div>
                  </div>
                </div>
                <TextAreaInput
                  v-model="in_note"
                  :label="$t('note')"
                  :placeholder="$t('note')"
                  :rules="no_rule"
                  :isError="
                    checkRulesIsError(in_note, 'add', this.no_rule).isError
                  "
                  :errorMsg="
                    checkRulesIsError(in_note, 'add', this.no_rule).errorMsg
                  "
                />
                <div>
                  <label
                    for="currency"
                    class="block mb-2 text-base font-medium text-textBlack1 dark:text-textWhite1"
                    >{{ $t("do_you_want_print_right_after_sale") }}</label
                  >
                  <div class="flex mx-4 mt-4">
                    <div class="flex items-center ml-4">
                      <input
                        id="inline-radio11"
                        type="radio"
                        value="1"
                        checked="true"
                        v-model="isPrint"
                        name="inline-radio-group1"
                        class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        @change="printStatus"
                      />
                      <label
                        for="inline-radio"
                        class="mr-2 text-base font-medium text-gray-900 dark:text-gray-300"
                        >{{ $t("cashier") }}</label
                      >
                    </div>
                    <div class="flex items-center ml-4">
                      <input
                        id="inline-3-radio11"
                        type="radio"
                        value="3"
                        v-model="isPrint"
                        name="inline-radio-group1"
                        class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        @change="printStatus"
                      />
                      <label
                        for="inline-radio"
                        class="mr-2 text-base font-medium text-gray-900 dark:text-gray-300"
                        >{{ $t("a4") }}</label
                      >
                    </div>
                    <div class="flex items-center ml-4">
                      <input
                        id="inline-2-radio11"
                        type="radio"
                        value="2"
                        v-model="isPrint"
                        @change="printStatus"
                        name="inline-radio-group1"
                        class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label
                        for="inline-2-radio"
                        class="mr-2 text-base font-medium text-gray-900 dark:text-gray-300"
                        >{{ $t("no") }}</label
                      >
                    </div>
                  </div>
                </div>

                <div>
                  <div class="w-full">
                    <label
                      for="fast_customer"
                      class="block mb-2 text-sm font-medium text-textBlack1 dark:text-textWhite1"
                      >{{ $t("fast_customer") }}</label
                    >
                  </div>
                  <div class="flex">
                    <BaseInput
                      v-model="customer_name"
                      :label="$t('customer_name')"
                      :placeholder="$t('customer_name')"
                      :rules="no_rule"
                      :isError="
                        checkRulesIsError(customer_name, 'add', this.no_rule)
                          .isError
                      "
                      :errorMsg="
                        checkRulesIsError(customer_name, 'add', this.no_rule)
                          .errorMsg
                      "
                    />
                    <div class="w-2"></div>

                    <BaseInput
                      v-model="customer_phone_number"
                      :label="$t('customer_phone_number')"
                      :placeholder="$t('customer_phone_number')"
                      :rules="no_rule"
                      :isError="
                        checkRulesIsError(
                          customer_phone_number,
                          'add',
                          this.no_rule
                        ).isError
                      "
                      :errorMsg="
                        checkRulesIsError(
                          customer_phone_number,
                          'add',
                          this.no_rule
                        ).errorMsg
                      "
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!--table-->
          <div class="relative overflow-x-auto mt-10 mb-6 overflow-y-auto">
            <!--in ltr change (text-right to text-left)-->
            <table class="w-full text-right text-gray-500 dark:text-gray-400">
              <thead
                class="text-xs text-gray-600 border-l-0 border-r-0 border border-gray-300 dark:border-gray-600 uppercase bg-slate-100 dark:bg-bgFullDark dark:text-gray-400"
              >
                <tr>
                  <th scope="col" class="px-2 py-1">{{ $t("item_name") }}</th>
                  <th scope="col" class="px-2 py-1">{{ $t("barcode") }}</th>
                  <!--   <th scope="col" class="px-2 py-1">{{ $t("cost") }}</th>-->
                  <th scope="col" class="px-2 py-1">{{ $t("price") }}</th>
                  <th scope="col" class="px-2 py-1">{{ $t("quantity") }}</th>
                  <th scope="col" class="px-2 py-1">{{ $t("bonus") }}</th>
                  <th scope="col" class="px-2 py-1">{{ $t("unit") }}</th>
                  <th scope="col" class="px-2 py-1" v-if="hasItemsWithPackages">{{ $t("packages") }}</th>
                  <th scope="col" class="px-2 py-1">{{ $t("discount") }}</th>
                  <th scope="col" class="px-2 py-1">{{ $t("final_price") }}</th>
                  <th scope="col" class="px-2 py-1">{{ $t("actions") }}</th>
                </tr>
              </thead>
              <tbody class="text-sm">
                <tr
                  class="bg-white text-textBlack border-b dark:bg-bgLightDark dark:text-textWhite2 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-bgFullDark"
                  v-for="(item, index) in getSoldListByCurrentCurrency"
                  :key="index"
                >
                  <td class="px-2 py-2 cursor-pointer"  @click="showPriceHistory(item)">
                    {{ item.i_name }}
                  </td>
                  <td class="px-2 py-2">
                    {{ item.i_barcode }}
                  </td>
                  <!--    <td class="px-2 py-2">
                    <select
                      class="bg-slate-100 border border-gray-300 text-textBlack1 placeholder-gray-400 text-base rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      v-model="item.pui_id"
                    >
                      <option
                        v-for="(item1, index) in item.purchase_items"
                        :key="index"
                        :value="item1.pui_id"
                      >
                        {{ item1.pui_id }}
                      </option>
                    </select>
                  </td>
                   -->
                  <td class="px-2 py-2 relative">
                    <input
                      type="number"
                      class="bg-slate-100 border border-gray-300 text-textBlack1 placeholder-gray-400 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      required
                      v-model="item.si_sale_price"
                      min="0"
                      @input="calculateDis(item, item.si_discount)"
                      
                    />
                  </td>
                  <td class="px-4 py-2">
                    <!-- For size items, show bottle quantity controls -->
                    <div v-if="item.selected_size" class="flex items-center gap-1">
                      <button
                        type="button"
                        @click.stop="decreaseBottleQuantity(index)"
                        class="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-2 py-1 rounded text-sm font-bold"
                        :disabled="item.bottle_quantity <= 1"
                      >
                        -
                      </button>
                      <input
                        type="number"
                        :ref="`quantity-input-${item.i_id}-${index}`"
                        class="bg-slate-100 border border-gray-300 text-textBlack1 placeholder-gray-400 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-14 p-2 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        required
                        v-model.number="item.bottle_quantity"
                        min="1"
                        @input="updateBottleQuantity(index, item.bottle_quantity)"
                      />
                      <button
                        type="button"
                        @click.stop="increaseBottleQuantity(index)"
                        class="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-2 py-1 rounded text-sm font-bold"
                      >
                        +
                      </button>
                    </div>
                    <!-- For regular items, show standard quantity input -->
                    <input
                      v-else
                      type="number"
                      :ref="`quantity-input-${item.i_id}`"
                      class="bg-slate-100 border border-gray-300 text-textBlack1 placeholder-gray-400 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-14 p-2 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      required
                      v-model="item.si_quantity"
                      min="0"
                      @input="calculateDue(item, item.si_quantity)"
                    />
                  </td>
                  <td class="px-2 py-2">
                    <!-- For size items, show bonus in bottles; for regular items, show directly -->
                    <input
                      v-if="item.selected_size && item.selected_size.is_size"
                      type="number"
                      class="bg-slate-100 border border-green-300 text-textBlack1 placeholder-gray-400 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block w-14 p-2 dark:bg-bgFullDark dark:border-green-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-green-500 dark:focus:border-green-500"
                      :value="getDisplayBonusQuantity(item)"
                      @input="updateBonusQuantity(item, $event)"
                      @blur="updateBonusQuantity(item, $event)"
                      min="0"
                      placeholder="0"
                      :title="$t('bonus_gift_items')"
                    />
                    <input
                      v-else
                      type="number"
                      class="bg-slate-100 border border-green-300 text-textBlack1 placeholder-gray-400 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block w-14 p-2 dark:bg-bgFullDark dark:border-green-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-green-500 dark:focus:border-green-500"
                      v-model="item.si_bonus_quantity"
                      min="0"
                      placeholder="0"
                      :title="$t('bonus_gift_items')"
                    />
                  </td>
                  <td class="px-2 py-2">
                    <!-- For size items, show the size -->
                    <div v-if="item.selected_size" class="flex items-center gap-2">
                      <span class="text-sm bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                        {{ item.selected_size.is_size }}
                      </span>
                    </div>
                    <span v-else>{{ getUnit(item) }}</span>
                  </td>
                  <td class="px-2 py-2" v-if="hasItemsWithPackages">
                    <div v-if="item.i_packages && item.i_packages.length > 0" class="space-y-1">
                      <div
                        v-for="(pkg, pkgIndex) in item.i_packages"
                        :key="pkgIndex"
                        class="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                        @click="setQuantityFromPackage(item, pkg.quantity, pkg.price)"
                        :title="$t('click_to_set_quantity')"
                      >
                        {{ pkg.name }}: {{ pkg.quantity }}+ = {{ formatNumber(pkg.price) }}
                      </div>
                    </div>
                    <span v-else class="text-gray-400 text-xs">
                      {{ $t("no_packages") }}
                    </span>
                  </td>
                  <td class="px-2 py-2">
                    <input
                      type="number"
                      class="bg-slate-100 border border-gray-300 text-textBlack1 placeholder-gray-400 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                      required
                      v-model="item.si_discount"
                      min="0"
                      @input="calculateDis(item, item.si_discount)"
                    />
                  </td>
                  <td class="px-2 py-2 relative">
                    <div 
                      class="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded"
                      @click="showPriceHistory(item)"
                    >
                      {{
                        formatNumber(
                          getItemPrice(item) -
                            item.si_discount
                        )
                      }}
                      <span v-if="item.si_bonus_quantity > 0" class="text-green-600 dark:text-green-400 text-xs block">
                        +{{ getDisplayBonusQuantity(item) }} {{ $t("free") }}
                      </span>
                      <span class="mx-1" v-show="cur_id == 1">{{
                        $t("dinar")
                      }}</span>
                      <span class="mx-1" v-show="cur_id == 2">{{
                        $t("dollar")
                      }}</span>
                    </div>
                  </td>
                  <td class="px-2 py-2 flex mt-2 gap-1">
                    <button type="button" @click="removeItemByIndex(index)">
                      <i
                        class="fa-solid fa-trash text-center p-1 w-6 h-6 text-xs bg-red-200 dark:bg-red-300 rounded-md text-red-500 mx-auto my-auto"
                      ></i>
                    </button>
                  </td>
                </tr>
                <tr
                  class="bg-white text-textBlack border-b dark:bg-bgLightDark dark:text-textWhite2 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-bgFullDark"
                  v-show="sold_list.length != 0"
                >
                  <td class="px-2 py-2"></td>
                  <td class="px-2 py-2"></td> 
                  <td class="px-2 py-2">
                    <div
                      class="text-sm font-medium text-gray-600 dark:text-gray-400"
                    >
                      {{ $t("total_without_discount") }}:
                    </div>
                    {{ formatPriceByCurrency(sumPriceWithoutDiscount) }}
                    <span class="mx-1" v-show="cur_id == 1">{{
                      $t("dinar")
                    }}</span>
                    <span class="mx-1" v-show="cur_id == 2">{{
                      $t("dollar")
                    }}</span>
                  </td>
                  <td class="px-2 py-2"></td>
                  <td class="px-2 py-2"></td>
                  <td class="px-2 py-2"></td>
                  <td class="px-2 py-2">
                    {{ $t("discount_amount") }} :
                    {{ formatPriceByCurrency(sumDiscount) }}
                    <span class="mx-1" v-show="cur_id == 1">{{
                      $t("dinar")
                    }}</span>
                    <span class="mx-1" v-show="cur_id == 2">{{
                      $t("dollar")
                    }}</span>
                  </td>
                  <td class="px-2 py-2">
                    {{ $t("total_price") }} : {{ formatPriceByCurrency(sumPrice) }}
                    <span class="mx-1" v-show="cur_id == 1">{{
                      $t("dinar")
                    }}</span>
                    <span class="mx-1" v-show="cur_id == 2">{{
                      $t("dollar")
                    }}</span>
                  </td>
                </tr>
                <tr
                  class="bg-white text-textBlack border-b dark:bg-bgLightDark dark:text-textWhite2 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-bgFullDark"
                  v-show="sold_list.length != 0"
                >
                  <td class="px-2 py-2"></td>
                  <td class="px-2 py-2"></td>
                  <td class="px-2 py-2"></td>
                  <td class="px-2 py-2"></td>
                  <td class="px-2 py-2"></td>
                  <td class="px-2 py-2">
                    <div class="flex flex-col space-y-2">
                      <div class="flex items-center space-x-2">
                        <label
                          class="text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          {{ $t("total_discount") }}:
                        </label>
                        <input
                          type="number"
                          class="bg-slate-100 border border-gray-300 text-textBlack1 placeholder-gray-400 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-20 p-1 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                          v-model="total_discount"
                          min="0"
                          @input="applyTotalDiscount"
                        />
                        <span class="text-xs" v-show="cur_id == 1">{{
                          $t("dinar")
                        }}</span>
                        <span class="text-xs" v-show="cur_id == 2">{{
                          $t("dollar")
                        }}</span>
                      </div>
                      <div class="flex items-center space-x-2">
                        <label
                          class="text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          {{ $t("percentage_discount") }}:
                        </label>
                        <input
                          type="number"
                          class="bg-slate-100 border border-gray-300 text-textBlack1 placeholder-gray-400 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-16 p-1 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                          v-model="percentage_discount"
                          min="0"
                          max="100"
                          @input="applyPercentageDiscount"
                        />
                        <span class="text-xs">%</span>
                      </div>
                    </div>
                  </td>

                  <td class="px-2 py-2 flex mt-2"></td>
                </tr>
                <tr
                  class="bg-white text-textBlack border-b dark:bg-bgLightDark dark:text-textWhite2 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-bgFullDark"
                  v-show="sold_list.length != 0"
                >
                  <td class="px-2 py-2"></td>
                  <td class="px-2 py-2"></td>

                  <td class="px-2 py-2"></td>
                  <td class="px-2 py-2"></td>
                  <td class="px-2 py-2"></td>

                  <td class="px-2 py-2 flex mt-2"></td>
                </tr>
              </tbody>
            </table>
            <center>
              <span
                class="text-center text-textBlack1 dark:text-white"
                v-show="sold_list.length == 0"
                >{{ $t("no_item_in_invoice") }}</span
              >
            </center>
          </div>

        </form>
      </div>
    </div>

    <div
      class="flex justify-between items-center px-4 md:px-16 py-3 left-0 right-0 fixed bottom-0 bg-white dark:bg-bgMeduimDark rounded-tl-xl z-50"
    >
      <div class="md:flex items-baseline">
        <p class="text-sm md:text-base text-textBlack1 dark:text-white">
          {{ $t("total_items_price") }} :
          <span class="text-xl md:text-2xl font-bold">{{
            formatPriceByCurrency(sumPrice)
          }}</span>
          <span class="mx-1 font-bold text-lg" v-show="cur_id == 1">{{
            $t("dinar")
          }}</span>
          <span class="mx-1 font-bold text-lg" v-show="cur_id == 2">{{
            $t("dollar")
          }}</span>
        </p>

        <div class="flex mx-0 md:mx-6 items-center my-3 lg:my-0">
          <span
            class="text-sm md:text-base font-medium text-textBlack1 dark:text-textWhite1 mx-1"
            >{{ $t("money_amount_received") }}:</span
          >
          <input
            type="number"
            class="bg-slate-100 border border-gray-300 text-textBlack1 placeholder-gray-400 text-lg rounded-lg focus:ring-blue-500 focus:border-blue-500 w-20 md:w-40 p-2 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            required
            v-model="in_paid"
            min="0"
          />
        </div>

        <p class="text-sm md:text-base mx-2 text-textBlack1 dark:text-white">
          {{ $t("debt") }}:
          <span class="text-xl md:text-2xl font-bold">{{
            formatNumber(sumPrice - in_paid)
          }}</span>
        </p>
      </div>

      <div class="lg:flex">
        <div class="flex justify-between mb-1 lg:mb-0">
          <button
            data-modal-target="defaultModal"
            data-modal-toggle="defaultModal"
            class="text-white px-2 text-lg lg:text-xl bg-blue-500 px-4 w-14 py-2 rounded items-center hover:shadow-md"
            @click="showModal(item)"
          >
            <i class="fa-solid fa-calculator"></i>
          </button>
          <div class="w-2 lg:w-4"></div>
          <button
            data-modal-target="defaultModal"
            data-modal-toggle="defaultModal"
            class="text-white px-2 text-lg lg:text-xl bg-red-500 w-14 py-2 lg:my-0 rounded items-center hover:shadow-md"
            @click="clearInvoice(true)"
          >
            <i class="fa-solid fa-trash"></i>
          </button>
        </div>
        <div class="w-2 lg:w-4"></div>
        <button
          class="text-white text-lg lg:text-xl bg-primaryColor w-32 lg:w-40 py-2 rounded items-center hover:shadow-md"
          @click="addSaleItem"
        >
          <div v-if="!isBtnLoading">{{ $t("sale") }}</div>

          <div
            class="flex items-center hover:shadow-mdtext-white flex justify-center items-center hover:shadow-md"
            role="status"
            v-else
          >
            <svg
              width="20"
              height="20"
              fill="currentColor"
              class="mr-2 animate-spin"
              viewBox="0 0 1792 1792"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M526 1394q0 53-37.5 90.5t-90.5 37.5q-52 0-90-38t-38-90q0-53 37.5-90.5t90.5-37.5 90.5 37.5 37.5 90.5zm498 206q0 53-37.5 90.5t-90.5 37.5-90.5-37.5-37.5-90.5 37.5-90.5 90.5-37.5 90.5 37.5 37.5 90.5zm-704-704q0 53-37.5 90.5t-90.5 37.5-90.5-37.5-37.5-90.5 37.5-90.5 90.5-37.5 90.5 37.5 37.5 90.5zm1202 498q0 52-38 90t-90 38q-53 0-90.5-37.5t-37.5-90.5 37.5-90.5 90.5-37.5 90.5 37.5 37.5 90.5zm-964-996q0 66-47 113t-113 47-113-47-47-113 47-113 113-47 113 47 47 113zm1170 498q0 53-37.5 90.5t-90.5 37.5-90.5-37.5-37.5-90.5 37.5-90.5 90.5-37.5 90.5 37.5 37.5 90.5zm-640-704q0 80-56 136t-136 56-136-56-56-136 56-136 136-56 136 56 56 136zm530 206q0 93-66 158.5t-158 65.5q-93 0-158.5-65.5t-65.5-158.5q0-92 65.5-158t158.5-66q92 0 158 66t66 158z"
              ></path>
            </svg>
            <span class="mx-1">{{ $t("wait") }}</span>
          </div>
        </button>
      </div>
    </div>
  </div>
  <confirm-dialogue ref="confirmDialogue"></confirm-dialogue>
  <ResultDialog ref="ResultDialog"></ResultDialog>
  <SizeSelectionModal ref="sizeSelectionModal"></SizeSelectionModal>
  <DynamicModalAction ref="actionModal"
    ><div class="relative w-full max-w-2xl max-h-full">
      <!-- Modal content -->
      <div class="relative">
        <!-- Modal header -->
        <div class="flex items-start justify-between p-4">
          <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
            {{ $t("change_money_counter") }}
          </h3>
          <i
            class="fa-solid fa-trash text-red-500"
            @click="recivedMoney = 0"
          ></i>
        </div>
        <!-- Modal body -->
        <div class="p-2 mb-14 md:mb-80 text-textBlack1 dark:text-white">
          <p>{{ $t("1_dollar_price") }} = {{ formatNumber(getDolarPrice) }}</p>
          <div class="md:flex w-full mt-2 rounded-lg">
            <div
              class="w-11/12 md:w-7/12 h-20 grid grid-cols-2 gap-x-2 gap-y-4 mx-auto"
            >
              <div
                class="flex flex-col bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(2, 1)"
              >
                <img
                  src="../../assets/image/1_dolar.jpg"
                  alt=""
                  class="w-full h-3/4 rounded-lg curser-pointer"
                />
                <p class="text-center my-auto curser-pointer">
                  {{ $t("1_dollar") }}
                </p>
              </div>
              <div
                class="flex flex-col bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(2, 5)"
              >
                <img
                  src="../../assets/image/5_dolar.png"
                  alt=""
                  class="w-full h-3/4 rounded-lg"
                />
                <p class="text-center my-auto">{{ $t("5_dollar") }}</p>
              </div>
              <div
                class="flex flex-col m-1 bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(2, 50)"
              >
                <img
                  src="../../assets/image/50dollar.jpeg"
                  alt=""
                  class="w-full h-3/4 rounded-lg"
                />
                <p class="text-center my-auto">{{ $t("50_dollar") }}</p>
              </div>
              <div
                class="flex m-1 flex-col bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(2, 100)"
              >
                <img
                  src="../../assets/image/100dollar.jpg"
                  alt=""
                  class="w-full h-3/4 rounded-lg"
                />
                <p class="text-center my-auto">{{ $t("100_dollar") }}</p>
              </div>
              <div
                class="flex flex-col m-1 bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(1, 1000)"
              >
                <img
                  src="../../assets/image/1_iqd.jpg"
                  alt=""
                  class="w-full h-3/4 rounded-lg"
                />
                <p class="text-center my-auto">{{ $t("1_thousand_dinar") }}</p>
              </div>
              <div
                class="flex flex-col m-1 bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(1, 5000)"
              >
                <img
                  src="../../assets/image/5dinnar.png"
                  alt=""
                  class="w-full h-3/4 rounded-lg"
                />
                <p class="text-center my-auto">{{ $t("5_thousand_dinar") }}</p>
              </div>
              <div
                class="flex flex-col m-1 bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(1, 10000)"
              >
                <img
                  src="../../assets/image/10dinnar.jpg"
                  alt=""
                  class="w-full h-3/4 rounded-lg"
                />
                <p class="text-center my-auto">{{ $t("10_thousand_dinar") }}</p>
              </div>
              <div
                class="flex flex-col m-1 bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(1, 25000)"
              >
                <img
                  src="../../assets/image/25dinnar.jpg"
                  alt=""
                  class="w-full h-3/4 rounded-lg"
                />
                <p class="text-center my-auto">{{ $t("25_thousand_dinar") }}</p>
              </div>
              <div
                class="flex flex-col m-1 bg-slate-100 dark:bg-bgMeduimDark rounded-lg w-36 h-24 md:w-40 md:h-28 shadow-md"
                @click="addRecievedMoney(1, 50000)"
              >
                <img
                  src="../../assets/image/50dinnar.png"
                  alt=""
                  class="w-full h-3/4 rounded-lg"
                />
                <p class="text-center my-auto">{{ $t("50_thousand_dinar") }}</p>
              </div>
            </div>
            <div class="h-40 md:mt-0"></div>
            <div class="w-full md:w-5/12 h-20 mt-96 md:mt-0">
              <div class="flex mb-2 justify-between">
                <span class="my-auto">{{ $t("items_price") }}</span>
                <input
                  type="number"
                  class="bg-slate-100 border border-gray-300 text-textBlack1 placeholder-gray-400 text-base rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-3/5 p-1.5 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  v-model="in_paid"
                />
              </div>

              <div class="flex mb-2 justify-between">
                <span class="my-auto">{{ $t("money_amount_received") }}</span>
                <input
                  type="number"
                  class="bg-slate-100 border border-gray-300 text-textBlack1 placeholder-gray-400 text-base rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-3/5 p-1.5 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  v-model="recivedMoney"
                />
              </div>

              <div class="flex mb-2 justify-between">
                <span class="my-auto">{{ $t("change_money") }} </span>
                <div class="w-2"></div>
                <input
                  type="number"
                  class="bg-slate-100 border border-gray-300 text-textBlack1 placeholder-gray-400 text-base rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-3/5 p-1.5 dark:bg-bgFullDark dark:border-gray-600 dark:placeholder-gray-200 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  v-model="getChangeMoney"
                />
              </div>
            </div>
          </div>
        </div>
      </div></div
  ></DynamicModalAction>
  <div :class="classesPrintModal()">
    <!-- modal -->
    <div class="my_font bg-white rounded shadow-lg w-10/12 md:w-1/3">
      <!-- modal body -->
      <div
        class="p-3 bg-white my_font font-bold text-size-base text-black"
        id="invoice_print"
      >
        <div>
          <h6 class="text-center text-black font-bold text-size-lg">
            {{ settings.app_name }}
          </h6>
          <p class="my_font text-center font-bold text-size-base text-black">
            {{ settings.set_header_invoice }}
          </p>
          <p class="my_font text-center font-bold text-size-base text-black">
            <i
              class="fas fa-phone text-size-base relative mx-2 text-pink text-center"
            ></i>
            {{
              settings.set_phone_number +
              (settings.set_phone_number2 != "" &&
              settings.set_phone_number2 != null
                ? " - " + settings.set_phone_number2
                : "")
            }}
          </p>
          <p class="my_font text-center font-bold text-size-base text-black">
            <i
              class="fas fa-map-marker-alt text-size-base relative mx-2 text-pink text-center"
            ></i>
            {{ settings.set_address }}
          </p>
        </div>
        <div class="w-full max-w-full shrink-0 md:w-full md:flex-0 bg-white">
          <div class="flex-auto pb-2 my_font">
            <div class="overflow-x-auto">
              <table
                class="items-center w-full mb-0 align-top border-gray-200 text-black-500"
              >
                <tbody>
                  <tr>
                    <td
                      class="text-black border border-black row-table-border font-bold px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ $t("invoice_id") }}
                    </td>
                    <td
                      class="text-black border border-black row-table-border font-bold px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ in_id }} 
                      
                    </td>
                  </tr>
                  <tr v-if="cu_id != ''">
                    <td
                      class="text-black border border-black row-table-border font-bold px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ $t("customer_name") }}
                    </td>
                    <td
                      class="text-black border border-black row-table-border font-bold px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{
                        customer_name == "" || customer_name == null
                          ? getCustomerName(cu_id) + " - " + cu_id
                          : customer_name + " - " + customer_phone_number
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td
                      class="text-black border border-black row-table-border font-bold px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ $t("date") }}
                    </td>
                    <td
                      class="text-black border border-black row-table-border font-bold px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      <div class="whitespace-normal">
                        {{ formatDate(created_at) }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="flex-auto pb-2">
            <div class="overflow-x-auto">
              <table
                class="items-center w-full mb-0 align-top border-gray-200 text-black-500"
              >
                <thead class="align-bottom text-center">
                  <tr>
                    <th
                      class="text-black text-right font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      #
                    </th>

                    <th
                      class="text-black text-right font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                      colspan="3"
                    >
                      {{ $t("item_name") }}
                    </th>
                    <th
                      class="text-black text-right font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ $t("price") }}
                    </th>
                    <th
                      class="text-black text-right font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ $t("quantity") }}
                    </th>
                    <th
                      class="text-black text-right font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                      v-if="totalBonusItems > 0"
                    >
                      {{ $t("bonus") }}
                    </th>

                    <th
                      class="text-black text-right font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                      v-if="sumDiscount > 0"
                    >
                      {{ $t("discount") }}
                    </th>
                    <th
                      class="text-black text-right font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ $t("total_price") }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    class="w-full"
                    v-for="(item, index) in getSoldListByCurrentCurrency"
                    :key="index"
                  >
                    <td
                      class="text-black -bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ index + 1 }}
                    </td>

                    <td
                      class="text-black font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-normal dark:border-white/40"
                      colspan="3"
                    >
                      <div class="whitespace-normal">
                        {{ getItemNameWithSize(item) }}
                      </div>
                    </td>
                    <td
                      class="text-black font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ formatNumber(getPriceByUnit(item)) }}
                    </td>
                    <td
                      class="text-black font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                    >
                      {{ formatNumber(getDisplayQuantity(item)) }}
                    </td>
                    <td
                      class="text-black font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                      v-if="totalBonusItems > 0"
                    >
                      {{ item.si_bonus_quantity > 0 ? formatNumber(getDisplayBonusQuantity(item)) : '-' }}
                    </td>
                    <td
                      class="text-black font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                      v-if="sumDiscount > 0"
                    >
                      {{ formatNumber(item.si_discount) }}
                    </td>
                    <td
                      class="text-black font-bold border border-black row-table-border my_font p-2 font-bold align-middle text-size-base bg-transparent border-b whitespace-nowrap dark:border-white/40"
                      colspan="1"
                    >
                      {{
                        formatNumber(
                          getItemPrice(item) -
                            item.si_discount
                        )
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div>
          <table
            class="items-center w-full mb-0 align-top border-gray-200 text-black-500"
          >
            <tbody>
              <tr>
                <td
                  class="text-black border border-black row-table-border px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <b>{{ $t("items_price") }}</b>
                </td>
                <td
                  class="text-black border border-black row-table-border px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <p>
                    {{ formatPriceByCurrency(sumPriceWithoutDiscount) }}

                    <span
                      class="mx-1 my_font text-black text-size-base"
                      v-show="cur_id == 1"
                      >{{ $t("dinar") }}</span
                    >
                    <span 
                      class="mx-1 my_font text-black text-size-base"
                      v-show="cur_id == 2"
                      >{{ $t("dollar") }}</span
                    >
                  </p>
                </td>
              </tr>
              <tr v-if="sumDiscount > 0">
                <td
                  class="text-black border border-black row-table-border px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <b>{{ $t("discount") }} </b>
                </td>
                <td
                  class="text-black text-size-base border border-black row-table-border px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <p>
                    {{ formatNumber(sumDiscount) }}
                    <span
                      class="mx-1 my_font text-black text-size-base"
                      v-show="cur_id == 1"
                      >{{ $t("dinar") }}</span
                    >
                    <span
                      class="mx-1 my_font text-black text-size-base"
                      v-show="cur_id == 2"
                      >{{ $t("dollar") }}</span
                    >
                  </p>
                </td>
              </tr>

              <!--end tr-->
              <tr>
                <td
                  class="text-black border border-black row-table-border px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <b>{{ $t("final_price") }}</b>
                </td>
                <td
                  class="text-black border border-black row-table-border px-4 py-2 w-1/5 text-size-lg my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <b> {{ formatPriceByCurrency(sumPrice) }}</b>
                  <span
                    class="mx-1 my_font text-black text-size-base"
                    v-show="cur_id == 1"
                    >{{ $t("dinar") }}</span
                  >
                  <span
                    class="mx-1 my_font text-black text-size-base"
                    v-show="cur_id == 2"
                    >{{ $t("dollar") }}</span
                  >
                </td>
              </tr>
              <tr v-if="getInDue > 0">
                <td
                  class="text-black border border-black row-table-border px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <b>{{ $t("debt") }}</b>
                </td>
                <td
                  class="text-black border border-black row-table-border px-4 py-2 w-1/5 text-size-lg my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <b> {{ formatNumber(getInDue) }}</b>
                  <span
                    class="mx-1 my_font text-black text-size-base"
                    v-show="cur_id == 1"
                    >{{ $t("dinar") }}</span
                  >
                  <span
                    class="mx-1 my_font text-black text-size-base"
                    v-show="cur_id == 2"
                    >{{ $t("dollar") }}</span
                  >
                </td>
              </tr>
              <tr v-if="getInDue > 0">
                <td
                  class="text-black border border-black row-table-border px-4 py-2 w-1/5 text-size-base my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <b>{{ $t("paid_money") }}</b>
                </td>
                <td
                  class="text-black border border-black row-table-border px-4 py-2 w-1/5 text-size-lg my_font p-2 align-middle bg-transparent border-b whitespace-nowrap dark:border-white/40"
                >
                  <b> {{ formatNumber(in_paid) }}</b>
                  <span
                    class="mx-1 my_font text-black text-size-base"
                    v-show="cur_id == 1"
                    >{{ $t("dinar") }}</span
                  >
                  <span
                    class="mx-1 my_font text-black text-size-base"
                    v-show="cur_id == 2"
                    >{{ $t("dollar") }}</span
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="border-b w-full"></div>
      <div class="flex justify-start items-center w-full p-3 bg-white">
        <button
          class="mx-auto text-center bg-pink-600 hover:bg-pink-700 px-3 py-1 rounded text-white"
          @click="modalPrintID"
        >
          <svg
            aria-hidden="true"
            class="mr-2 w-4 h-4 text-gray-200 animate-spin dark:text-gray-600 fill-pink-600"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            v-if="loading"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span v-if="!loading"> {{ $t("print") }}</span>
        </button>
      </div>
    </div>
  </div>
  <!-- Calculator Modal -->
  <CalculatorModal
    :isOpen="isCalculatorOpen"
    @close="closeCalculator"
    @open-calculator="openCalculator"
  />
 
  <DynamicModalAction ref="whatsAppDialog">
    <template #body>
      <div class="p-2">
        <TextAreaInput
          v-model="message.text"
          :placeholder="$t('Order message')"
          rows="8"
        />
      </div>
    </template>

    <template #footer>
      <button @click="handleWhatsAppMessageSubmit" class="btn btn-primary">
        {{ $t("create_order") }}
      </button>
    </template>
  </DynamicModalAction>

  <!-- Price History Dialog -->
  <div
    v-if="showPriceHistoryDialog"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    @click="closePriceHistoryDialog"
  >
    <div
      class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
      @click.stop
    >
      <!-- Header -->
      <div class="flex justify-between items-center p-4 border-b">
        <h3 class="text-lg font-semibold text-gray-800">
          {{ $t("last_3_prices") }}
        </h3>
        <button
          @click="closePriceHistoryDialog"
          class="text-gray-400 hover:text-gray-600"
        >
          <i class="fa-solid fa-times text-xl"></i>
        </button>
      </div>

      <!-- Content -->
      <div class="p-4">
        <div v-if="selectedItemForHistory" class="mb-4">
          <h4 class="font-medium text-gray-700 mb-2">
            {{ selectedItemForHistory.i_name }}
          </h4>
          <p class="text-sm text-gray-500">
            {{ $t("customer") }}: {{ getCustomerName({ id: cu_id }) }}
          </p>
        </div>

        <!-- History List -->
        <div v-if="cu_id && selectedItemForHistory && customerItemHistory[selectedItemForHistory.i_id] && customerItemHistory[selectedItemForHistory.i_id].length > 0">
          <div v-for="(history, idx) in customerItemHistory[selectedItemForHistory.i_id]" :key="idx" class="mb-3 p-3 bg-gray-50 rounded-lg">
            <div class="flex justify-between items-center mb-1">
              <div class="text-sm text-gray-600">
                {{ formatDate(history.created_at) }}
              </div>
              <div class="text-sm font-bold text-blue-600">
                {{ formatNumber(history.si_sale_price) }} {{ $t(history.cur_name) }}
              </div>
            </div>
            <div class="text-xs text-gray-500">
              {{ $t("invoice") }}: {{ history.in_id }}
            </div>
          </div>
        </div>

        <!-- No History Message -->
        <div v-else class="text-center py-8 text-gray-500">
          <i class="fa-solid fa-history text-4xl mb-2"></i>
          <p>{{ $t("no_purchase_history") }}</p>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end p-4 border-t">
        <button
          @click="closePriceHistoryDialog"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {{ $t("close") }}
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import BaseInput from "../../components/Inputs/BaseInput.vue";
import TextAreaInput from "../../components/Inputs/TextAreaInput.vue";
import { formatDate } from "../../utils/functions";
import ConfirmDialogue from "../../components/Modals/ConfirmDialogue.vue";
import ResultDialog from "../../components/Modals/ResultDialog.vue";
import { FILE_URL } from "../../constants/config";
import { CURRENCYS } from "../../constants/config";
import { formatNumber } from "../../utils/functions";
import DropdownInput from "../../components/Inputs/DropdownInput.vue";
import DynamicModalAction from "../../components/Modals/DynamicModalAction.vue";
import LoadingProgress from "../../components/loading-progress.vue";
import CalculatorModal from "../../components/Modals/CalculatorModal.vue";
import SizeSelectionModal from "../../components/Modals/SizeSelectionModal.vue";

//import html2pdf from "html2pdf.js";

export default {
  name: "ADD_SALE_TYPE",
  components: {
    BaseInput,
    ConfirmDialogue,
    ResultDialog,
    TextAreaInput,
    DropdownInput,
    DynamicModalAction,
    LoadingProgress,
    CalculatorModal,
    SizeSelectionModal,
  },
  data() {
    return {
      firstName: "",
      rules: [
        (value) => {
          if (value || value == 0) return true;

          return this.$t("you_must_fill_this_field");
        },
      ],
      rules_dropdown: [
        (value) => {
          if (value) return true;

          return this.$t("you_must_choose_an_option");
        },
      ],
      no_rule: [],
      inputType: "text",
      isSubmited: false,
      isSubmitedEdit: false,
      current: {},
      isShowDetail: false,
      file_url: FILE_URL,
      currencys: CURRENCYS,
      sold_list: [],
      barcode: "",
      in_paid: 0,
      in_price: 0,
      in_discount: 0,
      in_due: 0,
      total_discount: 0,
      percentage_discount: 0,
      cu_id: "",
      bo_id: "",
      cur_id: 1,
      st_id: "",
      in_main_currency_price: 0,
      pl_id: "",
      in_note: "",
      customer_name: "",
      customer_phone_number: "",
      created_at: "",
      tempItem: 0,
      recivedMoney: 0,
      changeMoney: 0,
      in_type: 1,
      isPrint: "2",
      isModalPrint: false,
      in_id: "",
      settings: {},
      isShowItemsCategorys: true,
      isShowCategories: true,
      isShowItems: true,
      message: {
        text: "",
      },
      beforeDebtIQD: 0,
      beforeDebtUSD: 0,
      isShowDebts: false,
      invoiceIdList: [],
      showTooltipForItem: null,
      showPriceHistoryDialog: false,
      selectedItemForHistory: null,
      lastAddedItemId: null,
    };
  },
  created() {
    this.getSaleTypes();
    this.getBoxs();
    
    this.$store.dispatch("item_category/getCategory");
    this.getPosData();

    this.getCustomers();

    this.getPlatforms();

    this.$store.dispatch("navbar/getItems");

    // Calculate initial total discount after a short delay to ensure data is loaded
    this.$nextTick(() => {
      setTimeout(() => {
        this.calculateInitialTotalDiscount();
      }, 100);
    });

    //settings=localStorage.getItem("currentSettingWiseManager");
    this.settings = JSON.parse(
      localStorage.getItem("currentSettingWiseManager")
    );
  },
  watch: {
    cu_id() {
      this.getCustomerDebt();
      this.clearCustomerItemHistory();
    },
    // eslint-disable-next-line no-unused-vars
    getResultDialog(newValue, oldValue) {
      if (newValue) {
        this.$refs.ResultDialog.show({
          message: this.$store.state.sale_pos.resultMsg,
          okButton: this.$t("done"),
          isSuccess: this.$store.state.sale_pos.isSuccess,
        });

        // Reset the deleteSuccess state after showing the dialog
        this.$store.commit("sale_pos/SET_DELETE_SUCCESS", false);
      }
    },
    sold_list: {
      handler() {
        // Update total discount when sold_list changes
        this.calculateInitialTotalDiscount();
      },
      deep: true,
    },
    isCalculatorOpen: false,
  },

  computed: {
    getChangeMoney() {
      return this.recivedMoney - this.in_paid;
    },
    getInDue() {
      return this.sumPrice - this.in_paid;
    },
    getResultDialog() {
      return this.$store.state.sale_pos.isShowResultDialog;
    },
    customerItemHistory() {
      return this.$store.state.sale_pos.customerItemHistory;
    },
    getDolarPrice() {
      return this.$store.state.navbar.dp_price;
    },
    filterItems() {
      return this.$store.getters["sale_pos/filterItems"];
    },
    filterItemsPos() {
      return this.$store.getters["sale_pos/filterItemsPOS"];
    },
    isDark() {
      return this.$store.state.theme == "dark";
    },
    isLoadingData() {
      return this.$store.state.sale_pos.isLoadingData;
    },
    isBtnLoading() {
      return this.$store.state.sale_pos.isBtnLoading;
    },
    isBtnEditLoading() {
      return this.$store.state.sale_pos.isBtnEditLoading;
    },
    convertToListIdValueCategory() {
      var categorys = this.$store.state.item_category.categorys;
      var list = [];
      for (const item of categorys) {
        list.push({
          id: item.ca_id,
          value: item.ca_name,
        });
      }

      return list;
    },
    convertToListIdValueBoxes() {
      var boxes = this.$store.state.all_box.boxes;
      var list = [];
      for (const item of boxes) {
        list.push({
          id: item.bo_id,
          value: item.bo_name,
        });
      }

      return list;
    },
    convertToListIdValueSaleTypes() {
      var saleTypes = this.$store.state.sale_type.saleTypes;
      var list = [];
      for (const item of saleTypes) {
        list.push({
          id: item.st_id,
          value: item.st_name,
        });
      }

      return list;
    },
    convertToListIdValuePlatforms() {
      var platforms = this.$store.state.sale_platform.platforms;
      var list = [];
      for (const item of platforms) {
        list.push({
          id: item.pl_id,
          value: item.pl_name,
        });
      }

      return list;
    },
    convertToListIdValue() {
      var list = [];
      for (const item of this.currencys) {
        list.push({
          id: item.cur_id,
          value: this.$t(item.cur_name),
        });
      }

      return list;
    },
    convertToListIdValueCustomers() {
      var customers = this.$store.state.customer.customers;
      var list = [];
      for (const item of customers) {
        list.push({
          id: item.cu_id,
          value: item.cu_name,
        });
      }

      return list;
    },
    sumDiscount() {
      var sum = 0;
      for (const item of this.getSoldListByCurrentCurrency) {
        sum += item.si_discount;
      }
      return Math.round(sum);
    },
    //sum price of all items
    sumPrice() {
      var sum = 0;
      for (const item of this.getSoldListByCurrentCurrency) {
        sum += this.getItemPrice(item) - item.si_discount;
      }

      // For dollar currency (cur_id == 2), show 2 decimal places
      // For dinar currency (cur_id == 1), round to whole number
      if (this.cur_id == 2) {
        // Dollar: keep 2 decimal places
        const roundedSum = parseFloat(sum.toFixed(2));
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.in_paid = roundedSum.toFixed(2);
        return roundedSum;
      } else {
        // Dinar: round to whole number
        const roundedSum = Math.round(sum);
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.in_paid = roundedSum.toFixed(0);
        return roundedSum;
      }
    },
    //price withoud discount
    sumPriceWithoutDiscount() {
      var sum = 0;
      for (const item of this.getSoldListByCurrentCurrency) {
        sum += this.getItemPrice(item);
      }
      // For dollar currency (cur_id == 2), show 2 decimal places
      // For dinar currency (cur_id == 1), round to whole number
      if (this.cur_id == 2) {
        return parseFloat(sum.toFixed(2));
      } else {
        return Math.round(sum);
      }
    },
    sumCost() {
      var sum = 0;
      for (const item of this.getSoldListByCurrentCurrency) {
        sum += this.getCostPriceByUnit(item) * item.si_quantity;
      }
      return sum.toFixed(2);
    },
    totalBonusItems() {
      var sum = 0;
      for (const item of this.getSoldListByCurrentCurrency) {
        sum += item.si_bonus_quantity || 0;
      }
      return sum;
    },
    hasItemsWithPackages() {
      return this.getSoldListByCurrentCurrency.some(item => 
        item.i_packages && item.i_packages.length > 0
      );
    },
    getSoldListByCurrentCurrency() {
      var list = [];
      for (const item of this.sold_list) {
        //cehck this.cur_id == item.cur_id nothing to do otherwise convert price if this.cur_id==1 convert to dinar if this.cur_id==2 convert to dollar
        list.push(item);
      }

      return list;
    },
    checkIfEdit() {
      if (this.$route.params.id) {
        return true;
      }
      return false;
    },
  },

  methods: {
    handleSearchInput(value) {
      this.$store.commit('sale_pos/SET_SEARCH_POS', value);
    },
    async buildInvoiceIdList() {
      try {
        const tempObj = { page: 1, rowsPerPage: 1000 };
        tempObj.search = "";
        tempObj.searchBarcode = "";
        const res = await this.$store.dispatch("sold_list/getItems", tempObj);
        if (res && res.data && Array.isArray(res.data.data)) {
          // ensure numeric sort ascending then we can pick last for latest
          this.invoiceIdList = res.data.data
            .map((x) => x.in_id)
            .sort((a, b) => a - b);
        }
      } catch (e) {
        console.error(e);
      }
    },
    async goToPrevInvoice() {
      // show full page loading immediately
      this.$store.state.loading = true;
      // Always navigate to the last (latest) invoice as requested
      if (!this.invoiceIdList.length) {
        await this.buildInvoiceIdList();
      }
      if (!this.invoiceIdList.length) return;
      const lastId = this.invoiceIdList[this.invoiceIdList.length - 1];
      if (lastId)
        this.$router.push({ name: "EditSale", params: { id: lastId } });
    },
    openCalculator() {
      console.log(
        "SalePos: openCalculator called, isLoadingData:",
        this.isLoadingData
      );
      // Prevent opening if page is still loading
      if (this.isLoadingData) {
        console.log("SalePos: Calculator blocked - page still loading");
        return;
      }

      this.isCalculatorOpen = true;

      // Ensure proper initialization after next tick
      this.$nextTick(() => {
        // Force re-render if needed
        this.$forceUpdate();
      });
    },
    closeCalculator() {
      this.isCalculatorOpen = false;
      this.$nextTick(() => {
        // Force re-render if needed
        this.$forceUpdate();
      });
    },
    async getCustomerDebt() {
      this.isShowDebts = false;
      try {
        var res = await this.$store.dispatch("customer/getCurrentDebt", {
          cu_id: this.cu_id,
          //invoice date
          created_at: new Date().toISOString(),
        });

        if (res && res.balance_before_date) {
          this.beforeDebtIQD = res.balance_before_date.IQD || 0;
          this.beforeDebtUSD = res.balance_before_date.USD || 0;
        } else {
          this.beforeDebtIQD = 0;
          this.beforeDebtUSD = 0;
        }
        this.isShowDebts = true;
      } catch (error) {
        console.error("Error fetching customer debt:", error);
        this.beforeDebtIQD = 0;
        this.beforeDebtUSD = 0;
        this.isShowDebts = true;
      }
    },
    async getItemPurchaseHistory(itemId) {
      // Only fetch if customer is selected
      if (!this.cu_id) {
        return;
      }

      // Don't fetch if we already have the data
      if (this.$store.state.sale_pos.customerItemHistory[itemId]) {
        return;
      }

      try {
        await this.$store.dispatch("sale_pos/getCustomerItemHistory", {
          cu_id: this.cu_id,
          i_id: itemId,
        });
      } catch (error) {
        console.error("Error fetching purchase history:", error);
      }
    },
    clearCustomerItemHistory() {
      // Clear customer item history when customer changes
      this.$store.state.sale_pos.customerItemHistory = {};
    },
    showPriceHistory(item) {
      this.selectedItemForHistory = item;
      this.showPriceHistoryDialog = true;
      // Fetch history if not already loaded
      this.getItemPurchaseHistory(item.i_id);
    },
    closePriceHistoryDialog() {
      this.showPriceHistoryDialog = false;
      this.selectedItemForHistory = null;
    },
    getCustomerName(cu_id1) {
      var customers = this.$store.state.customer.customers;

      for (const item of customers) {
        if (item.cu_id == cu_id1.id) {
          return item.cu_name;
        }
      }
    },
    showWhatsAppDialog() {
      this.$refs.whatsAppDialog.show();
    },
    handleWhatsAppMessageSubmit() {
      this.$refs.whatsAppDialog.hide();
      this.getOrderFromWhatsApp1();
    },
    getOrderFromWhatsApp1() {
      // Reset sold list
      this.sold_list = [];

      // Check if message text exists
      if (!this.message?.text || this.message.text.trim() === "") {
        this.$refs.ResultDialog.show({
          message: this.$t("no_message_found"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }

      // Updated regex to capture barcode, item name (with optional size), and quantity
      // Pattern: barcode -> item_name (with optional size like "30ml") *quantity دانە*
      const regExp = /(.+?)\s*->\s*([^*]+?)\s*\*\D*(\d+)\D*\*/gu;
      const matches = [...this.message.text.matchAll(regExp)];

      const orders = matches.map((match) => {
        const barcode = match[1].trim();
        const itemNameWithSize = match[2].trim(); // e.g., "میسک 30ml"
        const quantity = parseInt(match[3]);
        
        // Try to extract size from item name (e.g., "30ml", "50ml", "100ml")
        // Look for pattern: number followed by "ml" at the end
        const sizeMatch = itemNameWithSize.match(/(\d+\s*ml)\s*$/i);
        const sizeLabel = sizeMatch ? sizeMatch[1].trim() : null;
        
        return { barcode, quantity, sizeLabel };
      });

      if (orders.length === 0) {
        this.$refs.ResultDialog.show({
          message: this.$t("no_valid_orders_found"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }

      let notAvailable = [];

      orders.forEach((order) => {
        const item = this.filterItemsPos.find(
          (itm) => itm.i_barcode === order.barcode
        );

        if (item) {
          // Check if item has sizes
          let sizes = item.i_sizes;
          if (typeof sizes === 'string') {
            try {
              sizes = JSON.parse(sizes);
            } catch (e) {
              sizes = null;
            }
          }

          if (sizes && Array.isArray(sizes) && sizes.length > 0 && order.sizeLabel) {
            // Item has sizes and size was extracted from message
            // Find matching size
            const matchingSize = sizes.find(size => {
              const sizeStr = size.is_size ? size.is_size.toString().toLowerCase() : '';
              const orderSizeStr = order.sizeLabel ? order.sizeLabel.toLowerCase() : '';
              // Compare sizes (e.g., "30ml" vs "30 ml" or "30ml")
              return sizeStr.replace(/\s/g, '') === orderSizeStr.replace(/\s/g, '');
            });

            if (matchingSize) {
              // Add item with the matched size directly
              this.addItemWithSizeToInvoice(item, matchingSize, order.quantity);
            } else {
              // Size not found, show error
              notAvailable.push(`${order.barcode} (size ${order.sizeLabel} not found)`);
            }
          } else if (sizes && Array.isArray(sizes) && sizes.length > 0) {
            // Item has sizes but no size was extracted from message - show error
            notAvailable.push(`${order.barcode} (size required but not specified)`);
          } else {
            // Item without sizes - use existing logic
            if (item.i_quantity >= order.quantity) {
              for (let i = 0; i < order.quantity; i++) {
                this.addItemToInvoice(item);
              }
            } else {
              notAvailable.push(order.barcode);
            }
          }
        } else {
          notAvailable.push(order.barcode);
        }
      });

      if (notAvailable.length > 0) {
        this.$refs.ResultDialog.show({
          message:
            this.$t(
              "some_items_are_not_available_in_stock_please_check_the_following_barcodes"
            ) +
            ":\n" +
            notAvailable.join("\n"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
      }
    },
    addItemWithSizeToInvoice(item, size, quantity) {
      // Helper method to add item with a pre-selected size directly (from WhatsApp message)
      // Extract numeric value from size (e.g., "30ml" -> 30)
      const sizeValue = parseFloat(size.is_size.replace(/[^0-9.]/g, '')) || 0;
      const bottleQuantity = quantity;
      
      // Calculate price per bottle for this size (is_discount is the final discounted price)
      let sizePrice = size.is_price;
      if (size.is_discount && size.is_discount > 0 && size.is_discount < size.is_price) {
        sizePrice = size.is_discount; // Use discounted price (is_discount is the final price)
      }
      sizePrice = this.convertPriceToInvoiceCurrency(sizePrice || item.i_sale_price, item.cur_id);
      
      // Check if this exact size already exists in the invoice
      const existingSizeItem = this.sold_list.find(soldItem => 
        soldItem.i_id === item.i_id && 
        soldItem.selected_size && 
        soldItem.selected_size.is_size === size.is_size
      );
      
      if (existingSizeItem) {
        // Increment bottle quantity for existing size
        existingSizeItem.bottle_quantity += bottleQuantity;
        existingSizeItem.si_quantity = existingSizeItem.bottle_quantity * sizeValue;
      } else {
        // Create new item for this size
        const newSizeItem = {
          i_id: item.i_id,
          i_barcode: item.i_barcode,
          si_quantity: bottleQuantity * sizeValue, // Total quantity (bottles * size value)
          si_bonus_quantity: 0,
          cur_id: item.cur_id,
          si_sale_price: sizePrice, // Price per bottle
          si_discount: 0,
          i_name: item.i_name,
          un_name: item.unit.un_name,
          i_quantity: item.i_quantity,
          si_cost_price: item.i_cost_price,
          i_packages: item.i_packages,
          i_sizes: item.i_sizes, // Store all available sizes
          selected_size: size, // Store the selected size object
          bottle_quantity: bottleQuantity, // Number of bottles
          i_sale_price: item.i_sale_price,
        };

        this.sold_list.push(newSizeItem);
      }
    },
    modalPrintID() {
      this.isModalPrint = !this.isModalPrint;

      const printContents = document.getElementById("invoice_print").innerHTML;

      // Create a hidden iframe
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";

      document.body.appendChild(iframe);

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(`
    <html>
      <head>
        <title>Print</title>
        <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.16/dist/tailwind.min.css" rel="stylesheet">
        <style>
          body {
            direction: rtl;
            font-family: 'Rabar_022', sans-serif;
          }
        </style>
        <link rel="stylesheet" href="https://kcstore.online/fonts/Rabar_022.6835fa5e.ttf">
      </head>
      <body>
        <div>${printContents}</div>
      </body>
    </html>
  `);
      doc.close();

      // Wait a moment before printing
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          // Cleanup
          this.isModalPrint = !this.isModalPrint;
        }, 50); // slight delay ensures content is loaded
      };
    },
    classesPrintModal() {
      return {
        "modal  bg-opacity-5 h-full   w-full  fixed top-0 left-0  justify-center   hidden overflow-y-auto":
          !this.isModalPrint,
        "modal  bg-opacity-5 h-full   w-full  fixed top-0 left-0  justify-center  flex overflow-y-auto ":
          this.isModalPrint,
      };
    },
    getPosData() {
      this.sold_list = [];

      this.$store.dispatch("sale_pos/getItemsPOS", {
        in_type: this.in_type,
      });
    },
    printStatus() {},
    async changeCurrencyType() {
      if (this.sold_list.length == 0) {
        if (this.cur_id == 1) {
          this.cur_id = 2;
        } else {
          this.cur_id = 1;
        }
        return;
      }
      const ok = await this.$refs.confirmDialogue.show({
        message: this.$t("changing_currency_will_clear_invoice_are_you_sure"),
        okButton: this.$t("yes"),
      });

      if (!ok) {
        return;
      }
      this.sold_list = [];
      this.in_paid = 0;
      this.in_price = 0;
      this.in_discount = 0;
      this.in_due = 0;
      this.total_discount = 0;
      this.percentage_discount = 0;

      if (this.cur_id == 1) {
        this.cur_id = 2;
      } else {
        this.cur_id = 1;
      }
    },
    getUnit(item) {
      return item.un_name;
    },
    // Format price based on currency - dollar always shows 2 decimals, dinar can be whole number
    formatPriceByCurrency(value) {
      if (this.cur_id == 2) {
        // Dollar: always show 2 decimal places
        const num = parseFloat(value);
        if (isNaN(num)) return "0.00";
        return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      } else {
        // Dinar: use existing formatNumber behavior (removes .00)
        return this.formatNumber(value);
      }
    },
    getPriceByUnit(item) {
      // Check if item has packages and calculate package-based pricing
      if (item.i_packages && item.i_packages.length > 0) {
        // Convert base price to invoice currency for display
        const convertedBasePrice = this.convertPriceToInvoiceCurrency(item.i_sale_price, item.cur_id);
        return this.calculatePackagePrice(item, item.si_quantity, convertedBasePrice);
      }
      
      // Fallback to regular price (already converted when added to cart)
      return item.si_sale_price;
    },
    getItemPrice(item) {
      // For size items, use bottle_quantity instead of si_quantity
      if (item.selected_size && item.bottle_quantity !== undefined) {
        return item.bottle_quantity * this.getPriceByUnit(item);
      }
      // For regular items, use si_quantity
      return item.si_quantity * this.getPriceByUnit(item);
    },
    getDisplayQuantity(item) {
      // For size items, show bottle_quantity (number of bottles)
      if (item.selected_size && item.selected_size.is_size) {
        // If bottle_quantity is set, use it
        if (item.bottle_quantity !== undefined && item.bottle_quantity !== null) {
          return item.bottle_quantity;
        }
        // Otherwise, calculate from si_quantity and size value
        const sizeValue = parseFloat(item.selected_size.is_size.replace(/[^0-9.]/g, '')) || 0;
        if (sizeValue > 0 && item.si_quantity) {
          return Math.round(item.si_quantity / sizeValue);
        }
        // Fallback to 1 if calculation fails
        return 1;
      }
      // For regular items, show si_quantity
      return item.si_quantity;
    },
    getDisplayBonusQuantity(item) {
      // For size items, si_bonus_quantity is in ML, convert to bottles
      if (item.selected_size && item.selected_size.is_size) {
        const sizeValue = parseFloat(item.selected_size.is_size.replace(/[^0-9.]/g, '')) || 0;
        if (sizeValue > 0 && item.si_bonus_quantity > 0) {
          return Math.round(item.si_bonus_quantity / sizeValue);
        }
      }
      // For regular items, show si_bonus_quantity directly
      return item.si_bonus_quantity || 0;
    },
    getItemNameWithSize(item) {
      // If item has a selected size, append it to the name
      if (item.selected_size && item.selected_size.is_size) {
        return `${item.i_name} ${item.selected_size.is_size}`;
      }
      // For regular items, return name as is
      return item.i_name;
    },
    updateBonusQuantity(item, event) {
      // Update bonus quantity - for size items, convert bottles to ML for storage
      let bonusBottles = parseFloat(event.target.value) || 0;
      if (bonusBottles < 0) bonusBottles = 0;
      
      if (item.selected_size && item.selected_size.is_size) {
        // Convert bottle quantity to ML for storage
        const sizeValue = parseFloat(item.selected_size.is_size.replace(/[^0-9.]/g, '')) || 0;
        if (sizeValue > 0) {
          item.si_bonus_quantity = Math.round(bonusBottles * sizeValue);
        } else {
          item.si_bonus_quantity = bonusBottles;
        }
      } else {
        // Regular item - store directly
        item.si_bonus_quantity = bonusBottles;
      }
      // Trigger reactivity
      this.sold_list = [...this.sold_list];
    },
    
    // Helper method to convert price based on currency
    convertPriceToInvoiceCurrency(itemPrice, itemCurrencyId) {
      // Ensure itemPrice is a valid number
      const price = parseFloat(itemPrice) || 0;
      
      if (itemCurrencyId == this.cur_id) {
        return price;
      } else if (itemCurrencyId == 1) {
        // Item is in dinar, invoice is in dollar - convert to dollar
        return price / this.getDolarPrice;
      } else {
        // Item is in dollar, invoice is in dinar - convert to dinar
        return price * this.getDolarPrice;
      }
    },
    calculatePackagePrice(item, quantity, convertedBasePrice = null) {
      // Convert base price if not provided
      let basePrice = convertedBasePrice;
      if (basePrice === null) {
        basePrice = this.convertPriceToInvoiceCurrency(item.i_sale_price, item.cur_id);
      }
      
      // Ensure basePrice is a valid number
      basePrice = parseFloat(basePrice) || 0;
      
      // If no packages defined, return the converted base price
      if (!item.i_packages || item.i_packages.length === 0) {
        return parseFloat(basePrice.toFixed(2));
      }
      
      // Sort packages by quantity (descending) to find the best match
      const packages = [...item.i_packages].sort((a, b) => b.quantity - a.quantity);
      
      // Find the first package that matches the quantity
      for (const pkg of packages) {
        if (quantity >= pkg.quantity) {
          // Convert package price to invoice currency
          const convertedPackagePrice = this.convertPriceToInvoiceCurrency(pkg.price, item.cur_id);
          return parseFloat(convertedPackagePrice.toFixed(2));
        }
      }
      
      // If no package matches, return the converted base price
      return parseFloat(basePrice.toFixed(2));
    },
    getCostPriceByUnit(item) {
      return item.si_cost_price;
    },
    async getCustomers() {
      await this.$store.dispatch("customer/getCustomers");

      this.cu_id = this.$store.state.customer.customers[0].cu_id;
      this.getCustomerDebt();
    },
    async getPlatforms() {
      await this.$store.dispatch("sale_platform/getPlatforms");

      this.pl_id = this.$store.state.sale_platform.platforms[0].pl_id;
    },
    async getBoxs() {
      await this.$store.dispatch("all_box/getBoxes");

      this.bo_id = this.$store.state.all_box.boxes[0].bo_id;
    },
    async getSaleTypes() {
      await this.$store.dispatch("sale_type/getSaleTypes");

      this.st_id = this.$store.state.sale_type.saleTypes[0].st_id;
    },
    formatNumber(number) {
      return formatNumber(number);
    },
    formatDate(date) {
      return formatDate(date);
    },
    calculateDue(item, num) {
      this.updateInvoice(item, num);
    },
    calculateDis(item, discount) {
      this.updateInvoiceDiscount(item, discount);
    },
    setQuantityFromPackage(item, packageQuantity, packagePrice) {
      // Set the quantity to the package quantity and update the price
      // Find the cart item and update it directly
      for (const soldItem of this.sold_list) {
        if (soldItem.i_id == item.i_id) {
          soldItem.si_quantity = packageQuantity;
          // Convert package price to invoice currency
          const convertedPackagePrice = this.convertPriceToInvoiceCurrency(packagePrice, item.cur_id);
          soldItem.si_sale_price = parseFloat(convertedPackagePrice.toFixed(2));
          break;
        }
      }
      
      // Trigger reactivity
      this.sold_list = [...this.sold_list];
      
      // Recalculate discounts
      this.recalculateDiscounts();
    },
    async addSaleItem() {
      if (this.$store.state.sale_pos.isBtnLoading) return;

      this.isSubmited = true;
      if (
        this.checkRulesIsError(this.bo_id, "add", this.rules_dropdown)
          .isError ||
        this.checkRulesIsError(this.cu_id, "add", this.rules_dropdown)
          .isError ||
        this.checkRulesIsError(this.pl_id, "add", this.rules_dropdown)
          .isError ||
        this.checkRulesIsError(this.st_id, "add", this.rules_dropdown)
          .isError ||
        this.checkRulesIsError(this.cur_id, "add", this.rules).isError ||
        this.checkRulesIsError(this.in_paid, "add", this.rules).isError ||
        this.checkRulesIsError(this.sumPrice, "add", this.rules).isError ||
        this.checkRulesIsError(this.sumDiscount, "add", this.rules).isError ||
        this.checkRulesIsError(this.getInDue, "add", this.rules).isError ||
        this.checkRulesIsError(this.getDolarPrice, "add", this.rules).isError ||
        this.checkRulesIsError(this.sumCost, "add", this.rules).isError
      ) {
        this.$refs.ResultDialog.show({
          message: this.$t("please_fill_all_fields"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }
      //check if in paid > in price
      if (this.in_paid > this.sumPrice) {
        this.$refs.ResultDialog.show({
          message: this.$t("paid_money_more_than_total_price"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }
      //check if in_paid or in_price or in_discount or in_due is not number or < 0
      if (
        isNaN(this.in_paid) ||
        isNaN(this.in_price) ||
        isNaN(this.in_discount) ||
        isNaN(this.in_due) ||
        this.in_paid < 0 ||
        this.in_price < 0 ||
        this.in_discount < 0 ||
        this.in_due < 0
      ) {
        this.$refs.ResultDialog.show({
          message: this.$t("please_enter_valid_number_for_all_fields"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }

      if (this.sold_list.length == 0) {
        this.$refs.ResultDialog.show({
          message: this.$t("no_item_in_invoice"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }
      this.$store.state.sale_pos.isBtnLoading = true;
      var tempSoldList = [];
      for (const item of this.getSoldListByCurrentCurrency) {
        //find item in getSoldListByCurrentCurrency and get price and cost price
        
        // Calculate price - for size-based items, store price per ML
        let salePrice = this.getPriceByUnit(item);
        if (item.selected_size && item.selected_size.is_size) {
          // For size items, calculate price per ML (divide price by size value)
          const sizeValue = parseFloat(item.selected_size.is_size.replace(/[^0-9.]/g, '')) || 1;
          if (sizeValue > 0) {
            salePrice = salePrice / sizeValue; // Price per ML
          }
        }

        // Extract size_type from selected_size if it exists
        let sizeType = null;
        if (item.selected_size && item.selected_size.is_size) {
          const sizeValue = parseFloat(item.selected_size.is_size.replace(/[^0-9.]/g, '')) || 0;
          sizeType = sizeValue > 0 ? sizeValue.toString() : 'none';
        }
        
        tempSoldList.push({
          i_id: item.i_id,
          i_barcode: item.i_barcode,
          si_quantity: item.si_quantity,
          si_bonus_quantity: item.si_bonus_quantity || 0,
          cur_id: item.cur_id,
          si_sale_price: salePrice, // Price per ML for size items, price per unit for regular items
          si_discount: item.si_discount,
          si_cost_price: item.si_cost_price,
          pui_id: item.pui_id,
          size_type: sizeType, // Send size_type (e.g., "30", "50", "100" or null for none)
        });
      }

      // eslint-disable-next-line no-unreachable
      var res = await this.$store.dispatch("sale_pos/addNewItem", {
        cu_id: this.cu_id,
        bo_id: this.bo_id,
        cur_id: this.cur_id,
        st_id: this.st_id,
        pl_id: this.pl_id,
        in_paid: this.in_paid,
        in_price: this.sumPrice,
        in_cost: this.sumCost,
        in_discount: this.sumDiscount,
        in_due: this.getInDue,
        in_main_currency_price: this.getDolarPrice,
        in_note: this.in_note,
        customer_name: this.customer_name,
        customer_phone_number: this.customer_phone_number,
        sold_items: tempSoldList,
        created_at: this.created_at,
        in_type: this.in_type,
      });
      if (res.status == 200) {
        this.created_at = res.data.created_at;
        this.in_id = res.data.in_id;
        //cu_id
        this.cu_id = res.data.cu_id;
        //WAIT 500 MS

        if (this.isPrint == "1") {
          await new Promise((resolve) => setTimeout(resolve, 500));
          this.modalPrintID();
        } else if (this.isPrint == "3") {
          await new Promise((resolve) => setTimeout(resolve, 500));
          this.in_id = res.data.in_id;

          this.modalPrintIDA4(res.data.created_at);
        }
        this.$store.dispatch("navbar/getItems");

        if (this.isPrint == "2" || this.isPrint == "1") {
          this.getPosData();
          this.clearInvoice(false);
          this.isSubmited = false;
        }
        this.isSubmited = false;
        this.cu_id = this.$store.state.customer.customers[0].cu_id;
        this.pl_id = this.$store.state.sale_platform.platforms[0].pl_id;
        this.st_id = this.$store.state.sale_type.saleTypes[0].st_id;
      }
      if (this.isPrint == "2" || this.isPrint == "1") {
        this.$store.state.sale_pos.isBtnLoading = false;
      }
    },
    checkRulesIsError(value, type, rules) {
      var isError = false;
      var errorMsg = "";
      if (type == "add") {
        if (this.isSubmited == false)
          return {
            isError: false,
            errorMsg: "",
          };
      }
      if (type == "edit") {
        if (this.isSubmitedEdit == false)
          return {
            isError: false,
            errorMsg: "",
          };
      }

      for (const rule of rules) {
        if (rule(value) !== true) {
          isError = true;
          errorMsg = rule(value);
          // Stop the loop
          break;
        } else {
          isError = false;
          errorMsg = "";
        }
      }

      return {
        isError: isError,
        errorMsg: errorMsg,
      };
    },
    addRecievedMoney(curency, amount) {
      //check this.cur_id == curency.cur_id
      if (this.cur_id == curency) {
        this.recivedMoney += amount;
      } else {
        if (this.cur_id == 1) {
          //convert to dinar
          this.recivedMoney += amount * this.getDolarPrice;
        } else {
          //convert to dollar
          this.recivedMoney += amount / this.getDolarPrice;
        }
      }
    },

    async clearInvoice(isCheck) {
      if (isCheck) {
        const ok = await this.$refs.confirmDialogue.show({
          message: this.$t("are_you_sure_you_want_to_clear_the_cart"),
          okButton: this.$t("yes"),
        });

        if (!ok) {
          return;
        }
      }
      if (this.customer_name != null && this.customer_name != "") {
        //return all customer data to default
        this.getCustomers();
      }
      this.sold_list = [];
      this.in_paid = 0;
      this.in_price = 0;
      this.in_discount = 0;
      this.in_due = 0;
      this.total_discount = 0;
      this.percentage_discount = 0;
      this.in_main_currency_price = 0;
      this.in_note = "";
      this.customer_name = "";
      this.customer_phone_number = "";
      this.created_at = "";
      this.recivedMoney = 0;
    },
    async showModal(item) {
      this.current = item;
      await this.$refs.actionModal.show();
    },
    async addItemToInvoice(item) {
      //check if item is already in the list
      //check if expired date is not null and expired date < current date
      if (item.i_expire_date != null) {
        var today = new Date();
        var date = new Date(item.i_expire_date);
        if (date < today) {
          //show confirm dialog
          const ok = await this.$refs.confirmDialogue.show({
            message: this.$t(
              "this_item_is_expired_are_you_sure_want_to_add_it"
            ),
            okButton: this.$t("yes"),
          });

          if (!ok) {
            return;
          }
        }
      }
      if (item.i_quantity <= 0) {
        this.$refs.ResultDialog.show({
          message: this.$t("this_item_is_not_available"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }

      // Check if item has sizes - if yes, show size selection modal
      let sizes = item.i_sizes;
      if (typeof sizes === 'string') {
        try {
          sizes = JSON.parse(sizes);
        } catch (e) {
          sizes = null;
        }
      }

      if (sizes && Array.isArray(sizes) && sizes.length > 0) {
        // Show size selection modal
        const selectedSizes = await this.$refs.sizeSelectionModal.show({
          itemName: item.i_name,
          sizes: sizes,
          currencyName: this.$t(item.currency.cur_name),
        });

        // If user cancelled or didn't select any sizes, return
        if (!selectedSizes || selectedSizes.length === 0) {
          return;
        }

        // Calculate total quantity from selected sizes
        let totalQuantity = 0;
        selectedSizes.forEach(sizeSelection => {
          // Extract numeric value from size (e.g., "30ml" -> 30)
          const sizeValue = parseFloat(sizeSelection.is_size.replace(/[^0-9.]/g, '')) || 0;
          totalQuantity += sizeSelection.quantity * sizeValue;
        });

        // Check if we have enough quantity in stock
        if (totalQuantity > item.i_quantity) {
          this.$refs.ResultDialog.show({
            message: this.$t("not_enough_item_in_stock"),
            okButton: this.$t("done"),
            isSuccess: false,
          });
          return;
        }

        // Create a separate item for each selected size
        selectedSizes.forEach(sizeSelection => {
          // Extract numeric value from size (e.g., "30ml" -> 30)
          const sizeValue = parseFloat(sizeSelection.is_size.replace(/[^0-9.]/g, '')) || 0;
          const bottleQuantity = sizeSelection.quantity;
          
          // Calculate price per bottle for this size
          const sizePrice = this.convertPriceToInvoiceCurrency(sizeSelection.is_price || item.i_sale_price, item.cur_id);
          
          // Check if this exact size already exists in the invoice
          const existingSizeItem = this.sold_list.find(soldItem => 
            soldItem.i_id === item.i_id && 
            soldItem.selected_size && 
            soldItem.selected_size.is_size === sizeSelection.is_size
          );
          
          if (existingSizeItem) {
            // Increment bottle quantity for existing size
            existingSizeItem.bottle_quantity += bottleQuantity;
            existingSizeItem.si_quantity = existingSizeItem.bottle_quantity * sizeValue;
          } else {
            // Create new item for this size
            const newSizeItem = {
              i_id: item.i_id,
              i_barcode: item.i_barcode,
              si_quantity: bottleQuantity * sizeValue, // Total quantity (bottles * size value)
              si_bonus_quantity: 0,
              cur_id: item.cur_id,
              si_sale_price: sizePrice, // Price per bottle
              si_discount: 0,
              i_name: item.i_name,
              un_name: item.unit.un_name,
              i_quantity: item.i_quantity,
              si_cost_price: item.i_cost_price,
              i_packages: item.i_packages,
              i_sizes: item.i_sizes, // Store all available sizes
              selected_size: sizeSelection, // Store the selected size object
              bottle_quantity: bottleQuantity, // Number of bottles
              i_sale_price: item.i_sale_price,
            };

            this.sold_list.push(newSizeItem);
          }
        });
      } else {
        // No sizes - use existing logic
        var isExist = false;
        for (const soldItem of this.sold_list) {
          if (soldItem.i_id == item.i_id) {
            isExist = true;
            if (soldItem.si_quantity >= item.i_quantity) {
              this.$refs.ResultDialog.show({
                message: this.$t("not_enough_item_in_stock"),
                okButton: this.$t("done"),
                isSuccess: false,
              });
              return;
            }
            soldItem.si_quantity++;
            // Convert base price to invoice currency
            const convertedBasePrice = this.convertPriceToInvoiceCurrency(item.i_sale_price, item.cur_id);
            // Always update price based on quantity and packages with currency conversion
            soldItem.si_sale_price = this.calculatePackagePrice(item, soldItem.si_quantity, convertedBasePrice);
            break;
          }
        }

        if (isExist == false) {
          // Calculate the base price with currency conversion
          const convertedBasePrice = this.convertPriceToInvoiceCurrency(item.i_sale_price, item.cur_id);

          var obj = {
            i_id: item.i_id,
            i_barcode: item.i_barcode,
            si_quantity: 1,
            si_bonus_quantity: 0,
            cur_id: item.cur_id,
            si_sale_price: convertedBasePrice,
            si_discount: 0,
            i_name: item.i_name,
            un_name: item.unit.un_name,
            i_quantity: item.i_quantity,
            si_cost_price: item.i_cost_price,
            i_packages: item.i_packages,
            i_sale_price: item.i_sale_price,
          };
          
          // Always calculate price based on quantity and packages with currency conversion
          obj.si_sale_price = this.calculatePackagePrice(item, obj.si_quantity, convertedBasePrice);

          this.sold_list.push(obj);
        } else {
          this.sold_list = [...this.sold_list];
        }
      }

      // Fetch purchase history for this item
      this.getItemPurchaseHistory(item.i_id);

      // Recalculate discounts after adding item
      this.recalculateDiscounts();

      // Set last added item ID and focus on quantity input
      this.lastAddedItemId = item.i_id;
      this.$nextTick(() => {
        // Try to focus on the quantity input field
        const refName = `quantity-input-${item.i_id}`;
        const quantityInput = this.$refs[refName];
        
        if (quantityInput) {
          // Handle both array (if multiple refs exist) and single element
          const inputElement = Array.isArray(quantityInput) 
            ? quantityInput[quantityInput.length - 1] 
            : quantityInput;
          
          if (inputElement && inputElement.focus) {
            inputElement.focus();
            // Select all text so user can easily type a new number
            if (inputElement.select) {
              inputElement.select();
            }
          }
        }
      });
    },
    updateInvoice(item, num) {
      if (num > item.i_quantity) {
        this.$refs.ResultDialog.show({
          message: this.$t("not_enough_item_in_stock"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }
      for (const soldItem of this.sold_list) {
        if (soldItem.i_id == item.i_id) {
          soldItem.si_quantity = num;
          // Always update price based on quantity and packages with currency conversion
          // Convert the stored original price to invoice currency
          const convertedBasePrice = this.convertPriceToInvoiceCurrency(soldItem.i_sale_price, soldItem.cur_id);
          // Use the cart item's stored original price and packages
          const tempItem = {
            i_sale_price: soldItem.i_sale_price,
            i_packages: soldItem.i_packages,
            cur_id: soldItem.cur_id
          };
          soldItem.si_sale_price = this.calculatePackagePrice(tempItem, soldItem.si_quantity, convertedBasePrice);
          break;
        }
      }

      this.sold_list = [...this.sold_list];

      // Recalculate discounts after quantity change
      this.recalculateDiscounts();
    },
    updateInvoiceDiscount(item, discount) {
      if (discount > item.i_sale_price) {
        this.$refs.ResultDialog.show({
          message: this.$t("discount_more_than_item_price"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        return;
      }
      for (const soldItem of this.sold_list) {
        if (soldItem.i_id == item.i_id) {
          soldItem.si_discount = discount;
          break;
        }
      }

      this.sold_list = [...this.sold_list];
    },
    applyTotalDiscount() {
      if (this.sold_list.length === 0) {
        return;
      }

      // Clear percentage discount when using total discount
      this.percentage_discount = 0;

      // If total discount is 0 or negative, clear all item discounts
      if (this.total_discount <= 0) {
        for (const item of this.sold_list) {
          item.si_discount = 0;
        }
        this.sold_list = [...this.sold_list];
        return;
      }

      // Calculate total price without any discounts
      let totalPriceWithoutDiscount = 0;
      for (const item of this.sold_list) {
        totalPriceWithoutDiscount +=
          this.getItemPrice(item);
      }

      if (this.total_discount > totalPriceWithoutDiscount) {
        this.$refs.ResultDialog.show({
          message: this.$t("discount_more_than_total_price"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        this.total_discount = 0;
        return;
      }

      // Distribute the total discount proportionally among items
      let totalCalculatedDiscount = 0;
      for (const item of this.sold_list) {
        const itemPrice = this.getItemPrice(item);
        const proportionalDiscount =
          (itemPrice / totalPriceWithoutDiscount) * this.total_discount;
        item.si_discount = Math.round(proportionalDiscount * 100) / 100; // Round to 2 decimal places
        totalCalculatedDiscount += item.si_discount;
      }

      // Adjust the last item's discount to ensure total discount equals the desired amount
      if (this.sold_list.length > 0) {
        const difference = this.total_discount - totalCalculatedDiscount;
        const lastItem = this.sold_list[this.sold_list.length - 1];
        lastItem.si_discount =
          Math.round((lastItem.si_discount + difference) * 100) / 100;
      }

      this.sold_list = [...this.sold_list];
    },
    applyPercentageDiscount() {
      if (this.sold_list.length === 0) {
        return;
      }

      // Clear total discount when using percentage discount
      this.total_discount = 0;

      // If percentage discount is 0 or negative, clear all item discounts
      if (this.percentage_discount <= 0) {
        for (const item of this.sold_list) {
          item.si_discount = 0;
        }
        this.sold_list = [...this.sold_list];
        return;
      }

      // Validate percentage range
      if (this.percentage_discount > 100) {
        this.$refs.ResultDialog.show({
          message: this.$t("percentage_discount_cannot_exceed_100"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
        this.percentage_discount = 0;
        return;
      }

      // Apply percentage discount to each item
      let totalCalculatedDiscount = 0;
      for (const item of this.sold_list) {
        const itemPrice = this.getItemPrice(item);
        const discountAmount = (itemPrice * this.percentage_discount) / 100;
        item.si_discount = Math.round(discountAmount * 100) / 100; // Round to 2 decimal places
        totalCalculatedDiscount += item.si_discount;
      }

      // Round the total discount to a whole number and adjust the last item
      const roundedTotalDiscount = Math.round(totalCalculatedDiscount);
      if (
        this.sold_list.length > 0 &&
        roundedTotalDiscount !== totalCalculatedDiscount
      ) {
        const difference = roundedTotalDiscount - totalCalculatedDiscount;
        const lastItem = this.sold_list[this.sold_list.length - 1];
        lastItem.si_discount =
          Math.round((lastItem.si_discount + difference) * 100) / 100;
      }

      this.sold_list = [...this.sold_list];
    },
    recalculateDiscounts() {
      // Recalculate discounts when cart changes
      if (this.total_discount > 0) {
        this.applyTotalDiscount();
      } else if (this.percentage_discount > 0) {
        this.applyPercentageDiscount();
      }
    },
    calculateInitialTotalDiscount() {
      // Calculate total discount from all items when page loads
      if (this.sold_list.length > 0) {
        let totalDiscount = 0;
        for (const item of this.sold_list) {
          totalDiscount += item.si_discount || 0;
        }
        // Only update if there are actual discounts and we're not in the middle of applying a discount
        if (totalDiscount > 0 && this.percentage_discount === 0) {
          this.total_discount = Math.round(totalDiscount);
        }
      }
    },
    checkHowMuchItemInCard(i_id) {
      let totalBottles = 0;
      for (const item of this.sold_list) {
        if (item.i_id == i_id) {
          // For size items, count bottles (bottle_quantity)
          // For regular items, count quantity (si_quantity)
          if (item.selected_size && item.bottle_quantity !== undefined) {
            totalBottles += item.bottle_quantity;
          } else {
            totalBottles += item.si_quantity || 0;
          }
        }
      }
      return totalBottles;
    },
    removeItem(i_id) {
      for (const item of this.sold_list) {
        if (item.i_id == i_id && !item.selected_size) {
          this.sold_list.splice(this.sold_list.indexOf(item), 1);
          break;
        }
      }

      // Recalculate discounts after removing item
      this.recalculateDiscounts();
    },
    removeItemByIndex(index) {
      const list = this.getSoldListByCurrentCurrency;
      if (index >= 0 && index < list.length) {
        const item = list[index];
        const actualIndex = this.sold_list.indexOf(item);
        if (actualIndex >= 0) {
          this.sold_list.splice(actualIndex, 1);
          // Recalculate discounts after removing item
          this.recalculateDiscounts();
        }
      }
    },
    increaseBottleQuantity(index) {
      const list = this.getSoldListByCurrentCurrency;
      if (index >= 0 && index < list.length) {
        const item = list[index];
        if (item.selected_size && item.bottle_quantity !== undefined) {
          // Check stock availability
          const sizeValue = parseFloat(item.selected_size.is_size.replace(/[^0-9.]/g, '')) || 0;
          const newQuantity = (item.bottle_quantity + 1) * sizeValue;
          
          // Find the original item to check available stock
          const originalItem = this.filterItemsPos.find(itm => itm.i_id === item.i_id);
          if (originalItem) {
            // Calculate total quantity already in invoice for this item
            let totalUsedQuantity = 0;
            for (const soldItem of this.sold_list) {
              if (soldItem.i_id === item.i_id) {
                totalUsedQuantity += soldItem.si_quantity || 0;
              }
            }
            
            // Check if adding one more bottle would exceed stock
            if (newQuantity > originalItem.i_quantity - totalUsedQuantity + item.si_quantity) {
              this.$refs.ResultDialog.show({
                message: this.$t("not_enough_item_in_stock"),
                okButton: this.$t("done"),
                isSuccess: false,
              });
              return;
            }
          }
          
          item.bottle_quantity++;
          item.si_quantity = item.bottle_quantity * sizeValue;
          this.sold_list = [...this.sold_list];
          this.recalculateDiscounts();
        }
      }
    },
    decreaseBottleQuantity(index) {
      const list = this.getSoldListByCurrentCurrency;
      if (index >= 0 && index < list.length) {
        const item = list[index];
        if (item.selected_size && item.bottle_quantity !== undefined && item.bottle_quantity > 1) {
          const sizeValue = parseFloat(item.selected_size.is_size.replace(/[^0-9.]/g, '')) || 0;
          item.bottle_quantity--;
          item.si_quantity = item.bottle_quantity * sizeValue;
          this.sold_list = [...this.sold_list];
          this.recalculateDiscounts();
        }
      }
    },
    updateBottleQuantity(index, quantity) {
      const list = this.getSoldListByCurrentCurrency;
      if (index >= 0 && index < list.length) {
        const item = list[index];
        if (item.selected_size && item.bottle_quantity !== undefined) {
          const newQuantity = parseInt(quantity) || 1;
          if (newQuantity < 1) {
            item.bottle_quantity = 1;
          } else {
            const sizeValue = parseFloat(item.selected_size.is_size.replace(/[^0-9.]/g, '')) || 0;
            const newTotalQuantity = newQuantity * sizeValue;
            
            // Find the original item to check available stock
            const originalItem = this.filterItemsPos.find(itm => itm.i_id === item.i_id);
            if (originalItem) {
              // Calculate total quantity already in invoice for this item
              let totalUsedQuantity = 0;
              for (const soldItem of this.sold_list) {
                if (soldItem.i_id === item.i_id && soldItem !== item) {
                  totalUsedQuantity += soldItem.si_quantity || 0;
                }
              }
              
              // Check if new quantity would exceed stock
              if (newTotalQuantity > originalItem.i_quantity - totalUsedQuantity) {
                this.$refs.ResultDialog.show({
                  message: this.$t("not_enough_item_in_stock"),
                  okButton: this.$t("done"),
                  isSuccess: false,
                });
                // Revert to previous quantity
                item.bottle_quantity = item.si_quantity / sizeValue;
                this.sold_list = [...this.sold_list];
                return;
              }
            }
            
            item.bottle_quantity = newQuantity;
            item.si_quantity = newTotalQuantity;
          }
          this.sold_list = [...this.sold_list];
          this.recalculateDiscounts();
        }
      }
    },
    async addMoreSizesToItem(index) {
      const list = this.getSoldListByCurrentCurrency;
      if (index >= 0 && index < list.length) {
        const item = list[index];
        if (item.selected_size && item.i_sizes) {
          // Parse sizes if needed
          let sizes = item.i_sizes;
          if (typeof sizes === 'string') {
            try {
              sizes = JSON.parse(sizes);
            } catch (e) {
              sizes = null;
            }
          }
          
          if (sizes && Array.isArray(sizes) && sizes.length > 0) {
            // Get currency name - try from store or use default
            let currencyName = '';
            const currencies = this.$store.state.currency?.currencies || [];
            const currency = currencies.find(c => c.cur_id === item.cur_id);
            if (currency) {
              currencyName = this.$t(currency.cur_name);
            } else if (item.cur_id === 1) {
              currencyName = this.$t('dinar');
            } else if (item.cur_id === 2) {
              currencyName = this.$t('dollar');
            }

            // Show size selection modal
            const selectedSizes = await this.$refs.sizeSelectionModal.show({
              itemName: item.i_name,
              sizes: sizes,
              currencyName: currencyName,
            });

            // If user cancelled or didn't select any sizes, return
            if (!selectedSizes || selectedSizes.length === 0) {
              return;
            }

            // Find the original item to check stock
            const originalItem = this.filterItemsPos.find(itm => itm.i_id === item.i_id);
            if (!originalItem) {
              return;
            }

            // Calculate total quantity from new selections
            let totalQuantity = 0;
            selectedSizes.forEach(sizeSelection => {
              const sizeValue = parseFloat(sizeSelection.is_size.replace(/[^0-9.]/g, '')) || 0;
              totalQuantity += sizeSelection.quantity * sizeValue;
            });

            // Calculate current quantity in invoice for this item
            let currentQuantityInInvoice = 0;
            for (const soldItem of this.sold_list) {
              if (soldItem.i_id === item.i_id) {
                currentQuantityInInvoice += soldItem.si_quantity || 0;
              }
            }

            // Check if we have enough quantity in stock
            if (currentQuantityInInvoice + totalQuantity > originalItem.i_quantity) {
              this.$refs.ResultDialog.show({
                message: this.$t("not_enough_item_in_stock"),
                okButton: this.$t("done"),
                isSuccess: false,
              });
              return;
            }

            // Add new size items to the invoice
            selectedSizes.forEach(sizeSelection => {
              const sizeValue = parseFloat(sizeSelection.is_size.replace(/[^0-9.]/g, '')) || 0;
              const bottleQuantity = sizeSelection.quantity;
              
              // Calculate price per bottle for this size
              const sizePrice = this.convertPriceToInvoiceCurrency(sizeSelection.is_price || item.i_sale_price, item.cur_id);
              
              // Check if this exact size already exists in the invoice
              const existingSizeItem = this.sold_list.find(soldItem => 
                soldItem.i_id === item.i_id && 
                soldItem.selected_size && 
                soldItem.selected_size.is_size === sizeSelection.is_size
              );
              
              if (existingSizeItem) {
                // Increment bottle quantity for existing size
                existingSizeItem.bottle_quantity += bottleQuantity;
                existingSizeItem.si_quantity = existingSizeItem.bottle_quantity * sizeValue;
              } else {
                // Create new item for this size
                const newSizeItem = {
                  i_id: item.i_id,
                  i_barcode: item.i_barcode,
                  si_quantity: bottleQuantity * sizeValue,
                  si_bonus_quantity: 0,
                  cur_id: item.cur_id,
                  si_sale_price: sizePrice,
                  si_discount: 0,
                  i_name: item.i_name,
                  un_name: item.un_name,
                  i_quantity: item.i_quantity,
                  si_cost_price: item.si_cost_price,
                  i_packages: item.i_packages,
                  i_sizes: item.i_sizes,
                  selected_size: sizeSelection,
                  bottle_quantity: bottleQuantity,
                  i_sale_price: item.i_sale_price,
                };

                this.sold_list.push(newSizeItem);
              }
            });
            
            this.sold_list = [...this.sold_list];
            this.recalculateDiscounts();
          }
        }
      }
    },
    enterBarcode(e) {
      e.preventDefault();
      if (this.barcode == "") return;
      var item = this.filterItemsPos.find(
        (item) => item.i_barcode == this.barcode
      );
      if (item) {
        this.addItemToInvoice(item);
        this.barcode = "";
      } else {
        this.$refs.ResultDialog.show({
          message: this.$t("item_not_found_for_this_barcode"),
          okButton: this.$t("done"),
          isSuccess: false,
        });
      }
    },
    getItemImage(item) {
      if (!item || !item.i_images) {
        return "../../assets/image/image_not_found.png";
      }
      
      // Get i_images (multiple images array)
      let images = item.i_images;
      
      // If i_images is a JSON string, parse it
      if (typeof images === 'string') {
        try {
          images = JSON.parse(images);
        } catch (e) {
          return "../../assets/image/image_not_found.png";
        }
      }
      
      // If it's an array with at least one image, return the first image
      if (Array.isArray(images) && images.length > 0 && images[0]) {
        return this.file_url + images[0];
      }
      
      return "../../assets/image/image_not_found.png";
    },

    async modalPrintIDA4(created_at) {
      var res = await this.$store.dispatch("customer/getCurrentDebt", {
        cu_id: this.cu_id,
        //invoice date
        created_at: created_at,
      });

      var beforeDebtIQD = res.balance_before_date.IQD;
      var beforeDebtUSD = res.balance_before_date.USD;
      //balance_up_to_and_including_date
      var balanceUpToAndIncludingDateIQD =
        res.balance_up_to_and_including_date.IQD;
      var balanceUpToAndIncludingDateUSD =
        res.balance_up_to_and_including_date.USD;

      // Create a hidden iframe for printing
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";

      document.body.appendChild(iframe);

      const invoiceContent = `
    <html>
    <head>
      <title>${this.settings.app_name}</title>
      <style>
        /* Define the Kurdish font and apply it to the body */
        @font-face {
          font-family: 'kurd';
          src: url('https://storage.wisesale.app/Rabar_022-Busi21Jx.ttf') format('truetype');
        }

        body {
          font-family: 'kurd', sans-serif;
          direction: rtl; /* Set text direction to right-to-left */
          text-align: right;

          -webkit-print-color-adjust: exact; /* Force printing of background colors */
          print-color-adjust: exact;
          background: white;
          margin: 0;
        }

        /* Set A4 dimensions and margins for print */
        @page {
          size: A4;

        }

        .my_font { font-family: 'kurd' !important; }

        /* General styles for the invoice layout */
        .invoice-container {
          max-width: 210mm;
          margin: 0 auto;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
          padding: 10mm;
        }

        /* Header styles */
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 20px;

        }
        .headerBorder{
  border-bottom: 2px solid #eee;
        }
        .header h1 {
          font-size: 28px;
          color: #7165E3;
          margin: 0;
        }
        .header p {
          margin: 0;
          font-size: 12px;
          color: #777;
        }
        .header .company-info {
          text-align: right;
        }

        /* Details section styles */
        .details {
          display: flex;
          justify-content: space-between;
          margin-top: 20px;
          font-size: 13px;
        }
        .details h3 {
          margin: 0 0 5px 0;
          font-size: 15px;
          color: #7165E3;
        }

        /* Table styles */
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 20px;
        }
        th, td {
          border: 1px solid #eee;
          padding: 10px;
          text-align: center;
        }
        th {
          background-color: #7165E3;
          color: white;
        }
        tr:nth-child(even) {
          background-color: #f9f9f9;
        }

        /* Summary styles */
        .summary-box {
          margin-top: 20px;
          background: #f2f2f2;
          padding: 10px;
          border-radius: 5px;
        }
        .summary-box h3 {
          margin: 5px 0;
          color: #7165E3;
        }

        /* Footer styles */
        .footer {
          margin-top: 40px;
          text-align: center;
          font-size: 14px;
        }
      </style>
    </head>
    <body>
      <div class="invoice-container">
        <!-- HEADER -->
        <div class="header headerBorder">
          <div class="company-info">
            <h1>${this.settings.app_name}</h1>

            <p>${this.$t("phone number")}: ${
        this.settings.set_phone_number +
        (this.settings.set_phone_number2 != "" &&
        this.settings.set_phone_number2 != null
          ? " - " + this.settings.set_phone_number2
          : "")
      }</p>
                 <p>${this.$t("address")}: ${this.settings.set_address}</p>
          </div>
           <div class="company-logo">
            <img
              src="${this.file_url + this.settings.set_logo}"
              alt="Company Logo"
              style="max-width: 100px; max-height: 100px;"
            />
          </div>
          <div>
            <h1 style="color: #333; font-size: 20px;">${this.$t(
              this.settings.set_header_invoice
            )}</h1>
            <p><strong>${this.$t("invoice_id")}:</strong> ${
        this.in_id || "-"
      }</p>
            <p><strong>${this.$t("date")}:</strong> ${formatDate(
        created_at
      )}</p>
          </div>
        </div>
        <div class="header">
<h2 style="color: #333; font-size: 18px;">${
        this.$t("customer_name") +
        " : " +
        (this.customer_name == "" || this.customer_name == null
          ? this.cu_id + " - " + this.getCustomerName(this.cu_id)
          : this.customer_name + " - " + this.customer_phone_number)
      }</h2>

      </div>
        <!-- LINE ITEMS TABLE -->
        <table>
          <thead>
            <tr>
              <th>${this.$t("#")}</th>
              <th>${this.$t("item_name")}</th>
              <th>${this.$t("price_dana")}</th>
              <th>${this.$t("quantity")}</th>
              ${this.totalBonusItems > 0 ? `<th>${this.$t("bonus")}</th>` : ``}
              ${this.sumDiscount > 0 ? `<th>${this.$t("discount")}</th>` : ``}
              <th>${this.$t("total_price")}</th>
            </tr>
          </thead>
          <tbody>
            ${this.getSoldListByCurrentCurrency
              .map(
                (item) => `
              <tr>

                <td>${this.getSoldListByCurrentCurrency.indexOf(item) + 1}</td>
                <td>${this.getItemNameWithSize(item)}</td>
                <td>${this.formatNumber(this.getPriceByUnit(item))}</td>
                <td>${this.formatNumber(this.getDisplayQuantity(item))}</td>
                ${
                  this.totalBonusItems > 0
                    ? `<td>${item.si_bonus_quantity > 0 ? this.formatNumber(this.getDisplayBonusQuantity(item)) : '-'}</td>`
                    : ``
                }
                ${
                  this.sumDiscount > 0
                    ? `<td>${this.formatNumber(item.si_discount)}</td>`
                    : ``
                }
                <td>${this.formatNumber(
                  this.getItemPrice(item) -
                    item.si_discount
                )}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
  ${
    this.in_note
      ? `
    <div class="summary-box" style="margin: 10px 0 !important; padding: 8px !important;">
      <h3 style="margin: 0 0 5px 0 !important;">${this.$t("note")}</h3>
      <p style="margin: 0 !important;">${this.in_note}</p>
    </div>
    `
      : ``
  }
         <div class='header'>
          ${
            beforeDebtIQD > 0 ||
            beforeDebtIQD < 0 ||
            beforeDebtUSD > 0 ||
            beforeDebtUSD < 0 ||
            balanceUpToAndIncludingDateIQD > 0 ||
            balanceUpToAndIncludingDateIQD < 0 ||
            balanceUpToAndIncludingDateUSD > 0 ||
            balanceUpToAndIncludingDateUSD < 0
              ? `
          <div style="display: flex; justify-content: flex-end; margin-top: 20px;"

          >
  <div style="background-color: #f2f2f2; padding: 15px; border-radius: 5px; border: 1px solid #ddd; box-sizing: border-box; min-width: 200px;">


    <div style="padding: 4px 0; ">
      <div style="font-size: 14px; ">
        ${this.$t(
          "balance_before"
        )}: <div style="font-size: 14px; font-weight: bold;color: red;">
        ${formatNumber(beforeDebtIQD)} ${this.$t("dinar")}
      </div>
      </div>

      <div style="font-size: 14px; font-weight: bold;color: red;">
        ${formatNumber(beforeDebtUSD)} ${this.$t("dollar")}
      </div>
    </div>


    <div style="padding: 4px 0;  margin-top: 10px;">
      <div style="font-size: 14px; ">
        ${this.$t("balance_after")}:
      </div>
      <div style="font-size: 14px; font-weight: bold;color: red;">
        ${formatNumber(balanceUpToAndIncludingDateIQD)} ${this.$t("dinar")}
      </div>
      <div style="font-size: 14px; font-weight: bold;color: red;">
        ${formatNumber(balanceUpToAndIncludingDateUSD)} ${this.$t("dollar")}
      </div>
    </div>

  </div>
</div>
          `
              : ``
          }
        <div style="display: flex; justify-content: flex-end; margin-top: 20px;">
          <div style="; background-color: #f2f2f2; padding: 15px; border-radius: 5px; border: 1px solid #ddd; box-sizing: border-box;">
            <div style="display: flex; justify-content: space-between; padding: 4px 0;">
              <span style="font-size: 17px; font-weight: normal; color: #555;">${this.$t(
                "items_price"
              )}:</span>
              <span style="font-size: 17px; font-weight: normal; color: #333;">  ${this.formatNumber(
                this.sumPriceWithoutDiscount
              )} ${this.$t(this.cur_id == 1 ? "dinar" : "dollar")}

              </span>
            </div>
            ${
              this.sumDiscount > 0
                ? `
            <div style="display: flex; justify-content: space-between; padding: 4px 0;">
              <span style="font-size: 17px; font-weight: normal; color: #555;">${this.$t(
                "discount"
              )}:</span>
              <span style="font-size: 17px; font-weight: normal; color: #333;">${formatNumber(
                this.sumDiscount
              )}  ${this.$t(this.cur_id == 1 ? "dinar" : "dollar")}</span>
            </div>
            `
                : ``
            }
            <div style="display: flex; justify-content: space-between; padding: 4px 0; font-size: 18px; color: #333;">
              <span style=" color: #555;">${this.$t("final_price")}:</span>
              <span style=" font-weight: bold;">${formatNumber(
                this.sumPrice
              )}  ${this.$t(this.cur_id == 1 ? "dinar" : "dollar")}</span>
            </div>
            ${
              this.getInDue > 0
                ? `
            <div style="display: flex; justify-content: space-between; padding: 4px 0;">
              <span style="font-size: 18px; font-weight: normal; color: #555;">${this.$t(
                "paid_money"
              )}:</span>
              <span style="font-size: 18px; font-weight: bold; color: #008000;">${formatNumber(
                this.in_paid
              )}  ${this.$t(this.cur_id == 1 ? "dinar" : "dollar")}</span>
            </div>
            <div style="display: flex; justify-content: space-between; padding: 4px 0">
              <span style="font-size: 18px; ; color: #555;">${this.$t(
                "debt"
              )}:</span>
              <span style="font-size: 18px; font-weight: bold;; color: red;">${formatNumber(
                this.getInDue
              )}  ${this.$t(this.cur_id == 1 ? "dinar" : "dollar")}</span>
            </div>
            `
                : ``
            }
          </div>
        </div>



        </div>

      </div>
    </body>
    </html>
  `;

      // Write the generated HTML to the iframe
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(invoiceContent);
      doc.close();

      // Wait a moment before printing
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          // Cleanup
        }, 50); // slight delay ensures content is loaded
      };
      this.getPosData();
      this.clearInvoice(false);
      this.$store.state.sale_pos.isBtnLoading = false;
    },
  },
};
</script>
